import axios from 'axios';
import { ENDPOINTS } from './endpoints';
import { supabase } from '../lib/supabase';

// Booking module — wraps Supabase RPC responses in the legacy
// `{ data, error, message }` envelope the Express API returned so call sites
// like `setKpis(resp?.data ?? null)` keep working without changes.
// See docs/booking-module-supabase-migration-issues.md for which helpers
// are migrated vs. still pointing at Cloud Run.
const wrapSupabase = (data) => ({ error: false, data, message: 'ok' });
// `label` is an optional human-readable descriptor of the failing call (the RPC
// name, or a phrase like 'playbook detail'). The feature-level rpc helpers
// (ga4Rpc/eiRpc/ciRpc/cdpRpc/riRpc) all forward one; without it every failure
// surfaced as a bare "Supabase RPC failed" with no clue which call broke.
const wrapSupabaseError = (err, label) => {
  const detail = err?.message || 'Supabase RPC failed';
  const message = label ? `${label}: ${detail}` : detail;
  const error = new Error(message);
  error.response = { data: { error: true, message, data: null } };
  throw error;
};

// ─── Cold-plan statement-timeout retry ───────────────────────────────────────
// 2026-07-28: the parity re-capture caught `fn_get_ri_fuel_by_market` and
// `fn_get_ri_avg_gti` returning Postgres 57014 (statement_timeout) on the first
// cold call, then succeeding in under two seconds on the very next attempt — the
// cold plan blows the timeout, the warm plan does not. The frontend had no
// recovery path, so a user landing on Route Intelligence over a cold connection
// got a hard error where a single retry would have rendered the page.
// See .local/docs/RI_AIM_Develop_vs_Staging_Parity_2026-07-28.md §5.
//
// Deliberately narrow on three axes:
//   - only 57014. Auth, RLS and bad-param failures still surface on the first
//     attempt; retrying those only delays the error the user needs to see.
//   - only reads. A timeout on a write does not say whether it committed.
//   - only AIM and RI. Scoped back on 2026-07-29: the first version wired this
//     into the shared read path, which quietly changed behaviour in Google
//     Analytics, CDP, Event Intelligence, Competitive Intelligence and Google
//     Ads — five modules outside the finding that prompted it. Those keep their
//     original single-attempt behaviour. `getCdpInsights` has its own documented
//     57014 at >60-day ranges (see the note on that helper); if CDP wants this,
//     that should be its own decision.
//
// Trade-off within AIM/RI: a *deterministic* timeout now takes ~1s longer to
// surface. It fails either way, just a second later.
const STATEMENT_TIMEOUT_CODE = '57014';

// Default retry schedule for AIM/RI — the cold plan blows the timeout, the
// warm plan (cached on the very next call) does not. Tight delays are fine
// because only 1-2 RPCs fire per page there.
const RPC_RETRY_DELAYS_MS = [250, 750];

// CDP fires 4+ RPCs in parallel against `tracking_events` on page mount. On a
// cold cache they ALL scan from disk simultaneously, ALL blow statement_timeout
// (~3s), and the 250ms default retry fires while the first batch is still
// running — so the retry also hits a cold cache and times out. These longer
// delays give the first (timed-out) batch time to scan pages into Postgres
// shared buffers before the retry fires, so the retry finds pages cached and
// completes fast. Total worst-case: ~3s (first timeout) + 2s + 3s (retry
// timeout) + 4s + 3s (retry) ≈ 15s — but in practice the first retry after 2s
// succeeds because the pages are now in memory. This is the same pattern that
// makes the date-picker scenario work: by the time the user clicks it, the
// initial batch has already warmed the cache.
const CDP_RETRY_DELAYS_MS = [2000, 4000];

const isStatementTimeout = (err) =>
  err?.code === STATEMENT_TIMEOUT_CODE
  || /statement timeout/i.test(err?.message || '');

// 2026-09-04 — transport-level failures (HTTP/2 stream refused, connection
// reset by peer, "failed to fetch", "Load failed") surface as an error with
// NO Postgres `.code`. On a cold cache the CDP RPCs are slow enough to trip
// Supabase's edge/connection-pooler timeout BEFORE Postgres' statement_timeout
// (57014) fires, so the 57014-only retry never engaged — the error surfaced,
// the page caught it and set KPIs to null, and Visitor Intelligence rendered
// all zeros. Retrying these transport failures (the same recovery gscRpc uses
// for Search Console) lets the cold load self-recover the same way a manual
// date-picker toggle does: by attempt 2 the cache is warm and the connection
// is stable. Safe for reads only — same constraint as the 57014 retry.
//
// NB: a generic "network unreachable" deliberately does NOT match — it has no
// code but is not a transient stream/pooler failure, so it stays a hard fail
// (the established RI/AIM boundary).
const TRANSPORT_FAILURE = /failed to fetch|networkerror|load failed|err_http2|connection (closed|reset)|aborted/i;
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || '');

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryable = (err) => !!err && (isStatementTimeout(err) || isTransportFailure(err));

/**
 * Calls a read-only Supabase RPC, retrying when Postgres aborted the statement
 * on timeout (57014) OR when the call failed at the transport layer (HTTP/2
 * stream refused, connection reset, "failed to fetch"). Non-retryable errors
 * and successes return immediately.
 *
 * Transport failures are retried because on a cold cache a slow RPC can trip
 * Supabase's edge/connection-pooler timeout before Postgres' statement_timeout
 * — the error carries no Postgres `.code`, so without this retry it surfaces
 * immediately and the calling page renders all zeros (Visitor Intelligence's
 * cold-load symptom). By the retry the cache is warm and the connection stable,
 * the same recovery a manual date-picker toggle triggers.
 *
 * Returns Supabase's `{ data, error }` verbatim so callers keep the error
 * handling they already have. Safe for reads only — a timeout or transport
 * failure on a write is ambiguous about whether it committed, so write call
 * sites must opt out.
 *
 * The call is wrapped in try/catch, not a bare `await`: supabase.rpc() returns
 * a thenable PostgrestFilterBuilder whose rejection on a transport failure
 * throws from `await` (it has `then` but no `catch`), which would bypass the
 * retry loop and surface as an unhandled throw. Catching it normalises the
 * throw into a `{ data: null, error }` result the loop can inspect — the same
 * shape gscRpc already uses.
 *
 * @param {string} name RPC function name.
 * @param {object} params RPC arguments.
 * @param {number[]} [delays=RPC_RETRY_DELAYS_MS] Retry delay schedule in ms.
 *   One retry per entry; the last attempt is the one whose result is returned
 *   if all retries fail.
 * @returns {Promise<{data: any, error: any}>} The last attempt's result.
 */
export async function rpcReadWithRetry(name, params, delays = RPC_RETRY_DELAYS_MS) {
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  for (const ms of delays) {
    if (!isRetryable(result.error)) return result;
    await sleep(ms);
    result = await attempt();
  }
  return result;
}

// Helper function to get token. AuthContext.syncLegacyStorage keeps this in
// sync with the live Supabase session (including post-refresh), so reading
// from localStorage stays current without an async hop. If the storage value
// is ever ahead of/behind the live session, the response interceptor's 401
// handler below catches it and redirects to login.
const getAuthToken = () => localStorage.getItem('token') || '';

// AUTH-0: send both the new `Authorization: Bearer` form (preferred by the
// Supabase-aware backend middleware) and the legacy `x-access-token` header
// during the grace window. After Phase 6 retires the legacy verifier we can
// drop x-access-token.
const createHeaders = (token = null) => {
  const t = token || getAuthToken();
  return {
    Authorization: t ? `Bearer ${t}` : undefined,
    'x-access-token': t || undefined,
  };
};

// Helper function to clear auth data and redirect to login
const handleAuthExpired = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('username');
  localStorage.removeItem('role');
  localStorage.removeItem('client_id');
  localStorage.removeItem('user_id');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('avatar');
  localStorage.removeItem('theme_preference');
  
  // Only redirect if not already on login page
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// Axios response interceptor to handle expired tokens globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      const isLoginRequest = error.config?.url?.includes('/login');
      if (!isLoginRequest) {
        handleAuthExpired();
      }
    }
    return Promise.reject(error);
  }
);

// GCP-1 Wave A — 2026-07-06. Legacy /user/login accepted a username; Supabase
// Auth requires an email. Callers passing a username string will now get
// "invalid email" — LoginForm.js's placeholder text of "brian–insiderperks"
// is misleading and should be corrected in the UI copy. Preserve the legacy
// `{ data: { token, user } }` return shape so any surviving consumer keeps working.
export const loginUser = async (usernameOrEmail, password, rememberMe = false) => {
  void rememberMe; // Supabase session persistence is controlled by the client's storage config
  const { data, error } = await supabase.auth.signInWithPassword({ email: usernameOrEmail, password });
  if (error) throw { message: error.message };
  return { error: false, data: { token: data.session?.access_token, user: data.user }, message: 'ok' };
};

export const verifyToken = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw { message: error.message };
  if (!data.session) throw { message: 'No active session' };
  return { error: false, data: { user: data.session.user, token: data.session.access_token }, message: 'ok' };
};

// ─── GCP-1 Wave D — admin ops backlog ────────────────────────────────────
// These 10 helpers need admin-scoped operations (creating/updating/deleting
// other users' auth.users rows) that can only be done through a
// SUPABASE_SERVICE_ROLE_KEY. That key cannot ship to the browser, so each of
// these needs a Supabase Edge Function wrapping auth.admin.* — code not yet
// deployed. Until the `admin-user-ops` function exists in the Supabase
// project, these helpers reject early with a clear backlog marker so any
// consumer fails loudly instead of silently hitting the retired Cloud Run
// endpoint (which returns 403).
//
// See docs/gcp-decommission-epics.md § GCP-1 for the deploy plan.
// Guard against callers passing the literal string "undefined" / "null" as a
// UUID (from stale localStorage or a lifecycle race). Returns null so callers
// can drop the filter instead of shipping "undefined" to Postgres and getting
// 22P02 (invalid uuid input) back.
const safeUuid = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s || s === 'undefined' || s === 'null') return null;
  return s;
};

const BACKLOG_ERR = (helper) => ({
  message: `[BACKLOG] ${helper} depends on a backend deliverable that has not shipped yet.`,
  code: 'BACKEND_NOT_DEPLOYED',
});
const backlogStub = (helper) => async () => { throw BACKLOG_ERR(helper); };

// GCP-1 Deliverable #1 — 2026-07-08: admin-user-ops Edge Function shipped by
// backend. This wrapper builds the request body, invokes the function, and
// normalizes success/error into the legacy {error, data, message} envelope.
async function adminUserOps(action, params = {}, label) {
  const { data, error } = await supabase.functions.invoke('admin-user-ops', {
    body: { action, ...params },
  });
  if (error) {
    // 2026-08-08 (backend brief §4): admin-user-ops returns a specific message
    // on every failure path (duplicate email, invalid format, user not found,
    // rate limits…), but supabase-js surfaces non-2xx as a generic
    // "Edge Function returned a non-2xx status code" and stashes the real
    // response on error.context. Read it so the actual cause reaches the
    // screen instead of being thrown away.
    const detail = await error.context?.json?.().catch(() => null);
    throw {
      message: detail?.message || (data && data.message) || error.message || `${label} failed`,
      code: detail?.code || error.name,
    };
  }
  if (data && data.error) throw { message: data.message || `${label} failed`, code: data.code };
  return { error: false, data, message: data?.message || 'ok' };
}

// GCP-1 Deliverable #1 — 2026-07-08. Backend's admin-user-ops.register action
// creates the auth.users row + profile + org membership in one call. The 3
// role-specific helpers below all funnel through the same Edge Function with
// different p_role_name values, matching the legacy Cloud Run controllers.
//
// 2026-07-27 — Frontend workaround for orphan-user bug: the register action
// creates the auth.users + profiles rows but silently drops the org_id, so
// `organization_members` never gets a row and the user disappears from
// fn_admin_list_users (which filters by org membership). After register
// returns, we belt-and-suspenders call fn_admin_add_user_to_org to force the
// membership. Once backend fixes register to persist org_id, this becomes a
// no-op (the RPC upserts / returns "already a member" silently). Filed as
// G11 in GCP-DECOMMISSION-PART8-BACKEND-FOLLOWUPS.md.
async function registerAndAssign(action, params, label) {
  const resp = await adminUserOps(action, params, label);
  const userId = resp?.data?.user_id
    || resp?.data?.id
    || resp?.data?.user?.id
    || resp?.data?.data?.user_id
    || resp?.data?.profile?.id;
  if (userId && params.org_id) {
    const { error: rpcErr } = await supabase.rpc('fn_admin_add_user_to_org', {
      p_user_id: userId,
      p_org_id: params.org_id,
      p_role_name: params.role_name,
    });
    // Once backend register wires org_id itself, this call reports the user
    // is already a member — swallow only that case. Anything else means the
    // user IS created (register succeeded) but the org attachment failed,
    // which is exactly the orphan-user pattern we're trying to prevent —
    // surface it so the admin knows to fix or retry, don't silently warn.
    if (rpcErr) {
      const msg = rpcErr.message || '';
      const alreadyMember = /already|duplicate|exists/i.test(msg);
      if (!alreadyMember) {
        throw {
          message: `User created but organization assignment failed: ${msg}. The user exists in auth but has no org membership — they'll appear as an orphan in the list. Retry the assignment or fix backend register.`,
          code: 'register_but_orphaned',
          user_id: userId,
        };
      }
    }
  }
  return resp;
}

export const registerClient = (userData = {}) =>
  registerAndAssign('register', {
    email: userData.email,
    password: userData.password,
    first_name: userData.first_name,
    last_name: userData.last_name,
    display_name: userData.display_name,
    org_id: userData.org_id || userData.company_id,
    role_name: 'org_viewer',
  }, 'register client');

export const getUserDetails = async (userId) => {
  // If the caller is asking for the currently-logged-in user's own record,
  // resolve locally via fn_get_my_profile. Cross-user reads use the admin RPC.
  const { data: sess } = await supabase.auth.getSession();
  const authUserId = sess?.session?.user?.id;
  if (userId && String(userId) === String(authUserId)) {
    const { data, error } = await supabase.rpc('fn_get_my_profile');
    if (error) throw { message: error.message };
    const p = Array.isArray(data) ? data[0] : data;
    return { error: false, data: p, message: 'ok' };
  }
  // Cross-user path (platform_admin only): backend's fn_admin_get_user returns
  // { user, memberships, client_access }. Legacy consumers read the top-level
  // profile fields, so we spread `.user` up while preserving the extra keys.
  const { data, error } = await supabase.rpc('fn_admin_get_user', { p_user_id: userId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data: { ...(data?.user || {}), memberships: data?.memberships, client_access: data?.client_access }, message: 'ok' };
};

// GCP-2 — 2026-07-07 (v3). `fn_get_my_clients()` returns FULL rows with:
// { id, city, name, state, country, org_name, timezone, is_active,
//   public_id, organization_id, reservation_system, booking_engine_mode }.
// Does NOT include: campground_name, logo_url, ai_chatbot_enabled,
// ai_phone_agent_enabled, created_at, is_deleted, contact emails. Anything
// derived from those columns falls back to null/default here. If the UI
// needs those fields, backend must widen the RPC or grant SELECT on
// clients to authenticated users.
export const getFilteredClients = async (role = 3, page = 1, limit = 101) => {
  void role; void page; void limit;
  const { data, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: error.message };
  const shaped = (data || []).map((r) => ({
    id: r.id,
    first_name: r.name || '',            // display name lives in `name`
    last_name: '',
    display_name: r.name || '',
    email: '',                            // not exposed by the RPC
    company_id: r.organization_id || null,
  }));
  return { error: false, data: shaped, message: 'ok' };
};

export const getProjectsByClient = async (clientId, page = 1, limit = 10, type = null) => {
  const orgId = safeUuid(clientId);
  const { data: rpcRows, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: error.message };
  let rows = rpcRows || [];
  if (orgId) rows = rows.filter((r) => r.organization_id === orgId);
  // chat/voice filter unavailable — fn_get_my_clients doesn't expose the
  // ai_chatbot_enabled / ai_phone_agent_enabled columns. `type` is ignored;
  // the caller sees the full accessible set instead of a filtered subset.
  // This is a Rigsby-era distinction that only matters for the chat/voice
  // product split; when Rigsby is sunset this filter can be dropped upstream.
  void type;
  rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const total = rows.length;
  const from = (page - 1) * limit;
  const paged = rows.slice(from, from + limit);
  const shaped = paged.map((r) => ({
    id: r.id,
    name: r.name,
    type: 'chat',                        // default; RPC can't distinguish
    company_id: r.organization_id,
    logoUrl: null,                       // not exposed by the RPC
    status: r.is_active === false ? 0 : 1,
    ai_enabled: false,                   // not exposed
    created_at: null,
    org_name: r.org_name || null,
    city: r.city || null,
    state: r.state || null,
    country: r.country || null,
    timezone: r.timezone || null,
    reservation_system: r.reservation_system || null,
  }));
  return { error: false, data: shaped, pagination: { page, limit, total }, message: 'ok' };
};

export const getProjectDetails = async (id) => {
  const safeId = safeUuid(id);
  if (!safeId) throw { message: 'Invalid project id' };
  const { data, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: error.message };
  const row = (data || []).find((r) => r.id === safeId);
  if (!row) throw { message: 'Project not found' };
  return {
    error: false,
    data: {
      ...row,
      name: row.name,
      company_id: row.organization_id,
      logoUrl: null,
      status: row.is_active === false ? 0 : 1,
      ai_enabled: false,
      type: 'chat',
    },
    message: 'ok',
  };
};

// GCP-4 — 2026-07-07. Serialize a Supabase query into a CSV client-side.
// The legacy endpoint streamed CSV from the server; we now build it in the
// browser off the same conversations rows the list uses. For very wide ranges
// this pulls the whole set in-memory once — acceptable up to ~50k rows before
// backend needs a proper streaming export function.
const CSV_ESCAPE = (v) => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const downloadBlob = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * The title a downloaded transcripts export carries, shared by the CSV and the PDF
 * so both files sort together in a downloads folder and read the same in an email.
 *
 * "Transcripts <client id> Voice 2026-07-27 to 2026-08-25" — the modality word is
 * dropped entirely when the view is not scoped to one, rather than left as a gap.
 */
export const transcriptsExportTitle = (projectId, startDate, endDate, modality = null) => {
  const scope = modality ? `${String(modality).charAt(0).toUpperCase()}${String(modality).slice(1)} ` : '';
  return `Transcripts ${projectId} ${scope}${startDate} to ${endDate}`;
};

export const downloadTranscriptsCsv = async (projectId, startDate, endDate, modality = null, extraColumns = null) => {
  // 2026-07-28 — accept optional modality filter so CSV export honors the
  // ?modality=voice URL scope (was: exported everything regardless of the
  // filtered UI view, so the "CSV (N)" count didn't match what user saw).
  // 2026-07-29 — same end-of-day fix as getTranscripts, otherwise CSV
  // exports drop last-day calls made after midnight.
  //
  // `extraColumns` is an optional { headers: string[], cellsFor: (row) => string[] }
  // supplied by the caller — Voice Intelligence uses it to append outcome / stay
  // type / urgency / barriers / value, which live on fn_get_conversation_list and
  // not on this table. Injected rather than imported so the api layer keeps no
  // dependency on a component module.
  const endInclusive = /^\d{4}-\d{2}-\d{2}$/.test(String(endDate)) ? `${endDate}T23:59:59.999Z` : endDate;
  let q = supabase
    .from('conversations')
    // ai_tokens_used / ai_cost_cents are vendor internals and must not reach a
    // reader in any surface, an export included (the spec's R4). They were in
    // this select list; nothing in the CSV needs them.
    .select('id, kind, source, channel, modality, started_at, ended_at, duration_seconds, message_count, visitor_name, visitor_email, visitor_phone, tag, intent, sentiment, urgency, summary, has_booking, status')
    .eq('client_id', projectId)
    .gte('started_at', startDate)
    .lte('started_at', endInclusive);
  if (modality === 'chat' || modality === 'voice') q = q.eq('modality', modality);
  const { data, error } = await q.order('started_at', { ascending: false }).limit(50000);
  if (error) throw { message: error.message };
  const rows = data || [];
  const baseHeaders = Object.keys(rows[0] || { id: '' });
  const extraHeaders = Array.isArray(extraColumns?.headers) ? extraColumns.headers : [];
  const cellsFor = typeof extraColumns?.cellsFor === 'function' ? extraColumns.cellsFor : null;
  // Headers are read by a person in a spreadsheet, so they lose the storage
  // detail: "visitor_name" becomes "VISITOR NAME".
  const headerRow = [...baseHeaders, ...extraHeaders]
    .map((h) => String(h).replace(/_/g, ' ').trim().toUpperCase());
  const csv = [
    headerRow.join(','),
    ...rows.map((r) => {
      const base = baseHeaders.map((h) => CSV_ESCAPE(r[h]));
      const extra = cellsFor ? cellsFor(r).map(CSV_ESCAPE) : extraHeaders.map(() => '');
      return [...base, ...extra].join(',');
    }),
  ].join('\n');
  const filename = `${transcriptsExportTitle(projectId, startDate, endDate, modality)}.csv`;
  downloadBlob(csv, filename, 'text/csv;charset=utf-8');
  return { filename, row_count: rows.length };
};

// GCP-4 — 2026-07-07. Legacy /transcripts/list/all returned a
// `{ transcripts: [...], total, ... }` shape. We rehydrate from the
// `conversations` table (superset of chat + voice) using client_id.
// forceRefresh no longer applies (Supabase has no server cache).
export const getTranscripts = async (projectId, startDate, endDate, forceRefresh = false, opts = {}) => {
  void forceRefresh;
  const { offset = 0, limit = 100, modality = null } = opts;
  // 2026-07-28 — server-side modality filter so page counters + CSV export
  // reflect the visible slice. Was: server returned all N rows, client
  // filtered to K, CSV button still labelled "N" — confusing when user
  // deep-linked with ?modality=voice and expected the counts to agree.
  // 2026-07-29 — extend endDate to end-of-day so we don't drop calls made
  // later in the last day of the range. Without this, `.lte('2026-07-28')`
  // reads as `2026-07-28 00:00:00`, cutting off everything after midnight
  // and making the list undercount the tile (which uses 23:59:59).
  const endInclusive = /^\d{4}-\d{2}-\d{2}$/.test(String(endDate)) ? `${endDate}T23:59:59.999Z` : endDate;
  let q = supabase
    .from('conversations')
    .select('id, public_id, kind, source, source_id, modality, channel, started_at, ended_at, duration_seconds, message_count, human_message_count, bot_message_count, visitor_id, person_id, visitor_email, visitor_phone, visitor_name, tag, intent, sentiment, urgency, summary, has_booking, quality_score, status, priority, is_flagged, is_resolved, ai_tag_suggestion, ai_confidence, notes, phone_call_session_id, chatbot_session_id, created_at', { count: 'exact' })
    .eq('client_id', projectId)
    .gte('started_at', startDate)
    .lte('started_at', endInclusive);
  if (modality === 'chat' || modality === 'voice') q = q.eq('modality', modality);
  const { data, count, error } = await q
    .order('started_at', { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw { message: error.message };
  const rows = data || [];
  return {
    transcripts: rows,
    total: count || 0,
    offset,
    limit,
    // 2026-08-07 — explicit hasMore so paginated consumers don't have to derive
    // it. Both detail pages used to read a legacy `hasMore` key this function
    // never returned, so their load loops always stopped after the first batch
    // (the "infinite loading" bug on the chat/voice list).
    hasMore: offset + rows.length < (count || 0),
  };
};


// GCP-2 — 2026-07-07 (v2). RLS on `clients` returns 42501 for real user JWTs,
// so the earlier ".from('clients')" fallback is dead — always route through
// fn_my_accessible_clients (SECURITY DEFINER). Empty result means "user has no
// accessible clients" and should render an empty state, not fall through.
//
// 2026-07-28 (Part 9): backend widened `fn_get_my_clients` to include the
// AI product flags + branding fields. Consumers no longer need the admin-only
// fn_admin_get_client enrichment for the sidebar/tile gates.
export const getAllProjects = async (all = false) => {
  void all;
  const { data, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: error.message };
  const shaped = (data || []).map((r) => ({
    id: r.id,
    name: r.name || r.campground_name || '',
    // `type` is legacy (chat|voice split). The new AI flags are authoritative;
    // keep `type` populated so any residual `type === 'chat'` fallback code
    // still resolves the right way if AI flags are somehow both null.
    type: r.ai_phone_agent_enabled ? 'voice' : 'chat',
    company_id: r.organization_id || null,
    logoUrl: r.logo_url || null,
    logo_url: r.logo_url || null,
    status: r.is_active === false ? 0 : 1,
    ai_enabled: !!(r.ai_chatbot_enabled || r.ai_phone_agent_enabled),
    ai_chatbot_enabled: r.ai_chatbot_enabled ?? null,
    ai_phone_agent_enabled: r.ai_phone_agent_enabled ?? null,
    ai_phone_after_hours_only: r.ai_phone_after_hours_only ?? null,
    campground_name: r.campground_name || null,
    public_website: r.public_website || null,
    property_type: r.property_type || null,
    created_at: null,
    org_name: r.org_name || null,
    city: r.city || null,
    state: r.state || null,
    country: r.country || null,
    timezone: r.timezone || null,
    reservation_system: r.reservation_system || null,
    booking_engine_mode: r.booking_engine_mode || null,
    public_id: r.public_id || null,
  }));
  return { error: false, data: shaped, message: 'ok' };
};

// 2026-07-08: Deliverable #8 shipped. Aggregate conversation metrics from
// `conversations` — totals + averages. RPC returns { total, by_kind[],
// by_modality[], by_status[], has_booking, avg_duration_seconds, avg_messages,
// avg_quality_score }.
export const getInteractionCounts = async (projectId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_interaction_counts', {
    p_client_id: projectId,
    p_start_date: startDate || null,
    p_end_date: endDate || null,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

// 2026-07-08: Deliverable #8. Daily interaction trend. Recharts-ready.
export const getMetricsData = async (projectId, startDate, endDate, kind = null) => {
  const { data, error } = await supabase.rpc('fn_get_metrics_data', {
    p_client_id: projectId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_kind: kind,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

// 2026-07-08: Deliverable #8. Current vs equal-length prior period.
export const getGrowthComparison = async (projectId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_growth_comparison', {
    p_client_id: projectId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

// Fetch the ordered message turns for a single conversation. Routes through
// the SECURITY DEFINER `fn_get_conversation_detail` RPC — it handles both
// modalities internally (chat: conversation_messages; voice: phone_call_transcripts)
// and unifies the shape. Direct table reads 403 for authenticated users
// because RLS on phone_call_transcripts + conversation_messages isn't
// permissive; the RPC bypasses that (2026-07-27 verified live).
//
// Response shape: { conversation: {...}, messages: [{id, role, content, sent_at, ...}, ...] }
// Consumers already read via getMsg{Side,Text,Time} helpers which understand
// role/content/timestamp/created_at — we normalize to that lowest common set.
export const getConversationMessages = async (conversationId) => {
  if (!conversationId) return { error: false, data: [], message: 'no conversation id' };
  const { data, error } = await supabase.rpc('fn_get_conversation_detail', {
    p_conversation_id: conversationId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  const rows = Array.isArray(data?.messages) ? data.messages : (Array.isArray(data) ? data : []);
  const mapped = rows.map((m) => ({
    id: m.id,
    role: m.role,
    content: m.content,
    timestamp: m.sent_at || m.timestamp || m.created_at || null,
    sequence: m.sequence ?? m.turn_order ?? m.id,
  }));
  // 2026-08-06 — the Aug-4/5 user-turn repair appended recovered user messages
  // with NEW sequence values instead of inserting them at their chronological
  // positions, and fn_get_conversation_detail orders by sequence. Result: every
  // repaired conversation rendered all user messages bunched at the bottom.
  // sent_at survived with the true per-turn timestamps, so re-sort by timestamp
  // here (stable; sequence breaks ties; original index is the final fallback).
  const sorted = mapped
    .map((m, i) => ({ m, i }))
    .sort((a, b) => {
      const ta = a.m.timestamp ? Date.parse(a.m.timestamp) : NaN;
      const tb = b.m.timestamp ? Date.parse(b.m.timestamp) : NaN;
      const aHas = !Number.isNaN(ta);
      const bHas = !Number.isNaN(tb);
      if (aHas && bHas && ta !== tb) return ta - tb;
      if (aHas !== bHas) return aHas ? -1 : 1;
      const sa = Number(a.m.sequence);
      const sb = Number(b.m.sequence);
      if (!Number.isNaN(sa) && !Number.isNaN(sb) && sa !== sb) return sa - sb;
      return a.i - b.i;
    })
    .map(({ m }) => m);
  // 2026-08-08 (backend brief §3): the RPC returns a third top-level key
  // `call` — the full phone_call_sessions record for voice conversations
  // (null for chat, and for voice with no linked session). Pass it (and the
  // conversation row) through so the detail panes can render the call record.
  return {
    error: false,
    data: sorted,
    conversation: data?.conversation || null,
    call: data?.call || null,
    message: 'ok',
  };
};

// 2026-08-05 — Chat Ingestion Change Log §5: "Drive date pickers from
// fn_get_client_conversation_range rather than a cached value, since
// last_with_messages keeps moving." As the backfill works through 30 clients,
// conversations dated May/June/July arrive retroactively, and a fixed 30-day
// window misses them entirely. This RPC returns the client's actual
// conversation span so the date picker can default to a window that covers it.
//
// Shape: { chat: { first, last, total }, voice: { first, last, total } }
// NOTE (BUG-1 in CONVERSATIONS_BACKEND_UPDATE_VERIFICATION.md): an unauthorized
// caller gets zeros with no error key — indistinguishable from a genuinely
// empty client. Callers must treat total===0 as "fall back to 30d default",
// not "anchor on now()".
export const getClientConversationRange = async (clientId) => {
  if (!clientId) return { error: false, data: null, message: 'no client id' };
  const { data, error } = await supabase.rpc('fn_get_client_conversation_range', {
    p_client_id: clientId,
  });
  if (error) throw { message: error.message };
  // The RPC does not return an error envelope on auth denial (BUG-1), so we
  // can't branch on data?.error. Just pass the shape through; callers decide.
  return { error: false, data: data || null, message: 'ok' };
};

// GCP-4 — 2026-07-07. Activity feed comes from audit_log. When projectId is
// provided, filter to changes on rows tied to that client (record_id = client
// id OR table_name in {'clients','conversations'} with matching payload).
// Simpler pass here: recent audit_log rows unfiltered by project; the UI can
// filter/paginate client-side.
export const getRecentActivity = async (projectId, limit = 10) => {
  void projectId; // Filtering by project needs a JSONB lookup on new_values;
  // consumer UIs typically show a global feed. Add per-project view later
  // via `.contains('new_values', { client_id: projectId })` if needed.
  const { data, error } = await supabase
    .from('audit_log')
    .select('id, table_name, record_id, action, changed_fields, changed_by, change_source, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw { message: error.message };
  return { error: false, data: data || [], message: 'ok' };
};

// Helper function to get client data (assuming this exists in your system)
export const getClient = async (clientId) => {
  try {
    // This assumes you have a client details endpoint
    // Update this based on your actual client endpoint
    const response = await getUserDetails(clientId);
    return response.data || response;
  } catch (error) {
    throw error.response?.data || { message: "Failed to fetch client data" };
  }
};

// GCP-4 — 2026-07-07. fn_update_conversation RPC exists (SECURITY DEFINER
// enforces client_id scope via the caller's memberships). Legacy accepted
// arbitrary fields; the RPC expects a jsonb of updates.
export const updateConversation = async (conversationId, projectId, updateData) => {
  void projectId; // scope enforced by the RPC via auth.uid() → memberships
  const { data, error } = await supabase.rpc('fn_update_conversation', {
    p_conversation_id: conversationId,
    p_updates: updateData,
  });
  if (error) {
    // Fallback: direct table update if the RPC signature differs. RLS still
    // gates cross-client writes.
    const { data: upd, error: upErr } = await supabase
      .from('conversations')
      .update(updateData)
      .eq('id', conversationId)
      .select()
      .maybeSingle();
    if (upErr) throw { message: upErr.message };
    return { error: false, data: upd, message: 'Conversation updated' };
  }
  return { error: false, data, message: 'Conversation updated' };
};

// 2026-07-08: Deliverable #6 shipped. All three actions go through the
// `ai-ops` Edge Function which wraps Gemini/OpenAI with the service-role key.
async function aiOps(action, params, label) {
  const { data, error } = await supabase.functions.invoke('ai-ops', {
    body: { action, ...params },
  });
  if (error) throw { message: (data && data.message) || error.message || `${label} failed`, code: error.name };
  if (data && data.error) throw { message: data.message || `${label} failed`, code: data.code };
  return { error: false, data, message: data?.message || 'ok' };
}

export const analyzeConversationTag = async (conversationId) => (
  aiOps('analyze', { conversation_id: conversationId }, 'analyze')
);

export const generateAiSummary = async (conversationId) => (
  aiOps('summarize', { conversation_id: conversationId }, 'summarize')
);

export const batchAnalyzeConversations = async (clientId, { limit = 50 } = {}) => (
  aiOps('batch_analyze', { client_id: clientId, limit }, 'batch_analyze')
);

// 2026-07-08: Deliverable #7. Aggregate AI token/cost usage.
export const getAiUsageStats = async (projectId, startDate = null, endDate = null) => {
  const { data, error } = await supabase.rpc('fn_get_ai_usage_stats', {
    p_client_id: projectId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

// User Management APIs
// GCP-1 Wave D — 2026-07-07 (v3).
// Direct `profiles` table select triggered a database RLS bug: policy on
// `organization_members` recurses into itself → 42P17 (infinite recursion).
// Route through SECURITY DEFINER RPCs which bypass RLS server-side and
// also give us the auth.users.email (joined server-side, unreachable from
// the browser via table selects).
//
//   fn_get_my_organizations()          → orgs the caller belongs to
//   fn_list_org_members(p_org_id uuid) → members of one org (with email)
//
// Merge across every org the caller has access to and dedupe by user_id.
// Role mapping: fn_list_org_members returns role_name (org_viewer/analyst/
// admin/owner/platform_admin) — translate to the legacy numeric role
// (0=super_admin, 1=admin, 2=manager, 3=client) that consumer UIs still
// use for gating.
const ROLE_NAME_TO_LEGACY = {
  platform_admin: 0,
  org_owner: 1,
  org_admin: 1,
  org_analyst: 2,
  org_viewer: 3,
};

// 2026-07-08: rewritten on top of fn_admin_list_users. For platform_admin the
// RPC returns every profile in one call; for scoped roles it returns members
// of every org they belong to. Old org-fanout path retained as fallback if the
// admin RPC returns "Platform admin required" for a non-admin caller (in which
// case fn_get_my_organizations + fn_list_org_members is the correct surface).
// Role name can arrive under several keys depending on which RPC produced the
// row (fn_admin_list_users vs fn_list_org_members vs fn_admin_get_user). Read
// them all and pick the strongest role — platform_admin > org_owner > org_admin
// > org_analyst > org_viewer — so a multi-org user's admin role isn't hidden
// behind a viewer role from another org.
const ROLE_STRENGTH = {
  platform_admin: 4,
  org_owner: 3,
  org_admin: 3,
  org_analyst: 2,
  org_viewer: 1,
};
const resolveRoleName = (m) => {
  const candidates = [];
  if (m?.role_name) candidates.push(m.role_name);
  if (m?.role && typeof m.role === 'string') candidates.push(m.role);
  if (m?.primary_role) candidates.push(m.primary_role);
  if (Array.isArray(m?.roles)) {
    for (const r of m.roles) {
      if (typeof r === 'string') candidates.push(r);
      else if (r?.role_name) candidates.push(r.role_name);
    }
  }
  if (Array.isArray(m?.memberships)) {
    for (const mem of m.memberships) if (mem?.role_name) candidates.push(mem.role_name);
  }
  let best = null;
  for (const c of candidates) {
    if (ROLE_STRENGTH[c] != null && (best === null || ROLE_STRENGTH[c] > ROLE_STRENGTH[best])) best = c;
  }
  return best;
};

const shapeAdminUserRow = (m) => {
  const displayName = m.display_name || [m.first_name, m.last_name].filter(Boolean).join(' ');
  const roleName = resolveRoleName(m);
  return {
    id: m.user_id || m.id,
    profile_id: m.user_id || m.id,
    email: m.email || '',
    first_name: m.first_name || '',
    last_name: m.last_name || '',
    display_name: displayName,
    avatar: m.avatar_url || null,
    role: roleName != null ? ROLE_NAME_TO_LEGACY[roleName] ?? 3 : 3,
    role_name: roleName,
    status: m.status === 'active' || m.status === 1 ? 1 : 0,
    company_id: m.org_id || m.organization_id || null,
    joined_at: m.joined_at || m.created_at || null,
    last_seen_at: m.last_seen_at || null,
    is_orphan: m.is_orphan === true,
  };
};

export const getAllUsers = async () => {
  const adminRes = await supabase.rpc('fn_admin_list_users', { p_org_id: null, p_page: 1, p_limit: 1000, p_include_orphans: true });
  if (!adminRes.error && !adminRes.data?.error) {
    const rows = Array.isArray(adminRes.data) ? adminRes.data : (adminRes.data?.users || []);
    const shaped = rows.map(shapeAdminUserRow).sort((a, b) =>
      (a.display_name || '').localeCompare(b.display_name || '')
    );
    return { error: false, data: shaped, message: 'ok' };
  }
  // Fallback for non-admins: fan-out fn_list_org_members across their orgs.
  const { data: orgs, error: orgErr } = await supabase.rpc('fn_get_my_organizations');
  if (orgErr) throw { message: orgErr.message };
  const orgIds = (orgs || []).map((o) => o.id || o.org_id || o.organization_id).filter(Boolean);
  if (!orgIds.length) return { error: false, data: [], message: 'ok' };
  const results = await Promise.all(
    orgIds.map((orgId) =>
      supabase.rpc('fn_list_org_members', { p_org_id: orgId }).then((r) => (
        (r.data || []).map((m) => ({ ...m, org_id: orgId }))
      ))
    ),
  );
  const seen = new Map();
  for (const list of results) {
    for (const m of list) {
      const key = m.user_id || m.membership_id;
      if (!key || seen.has(key)) continue;
      seen.set(key, shapeAdminUserRow(m));
    }
  }
  const shaped = Array.from(seen.values()).sort((a, b) =>
    (a.display_name || '').localeCompare(b.display_name || '')
  );
  return { error: false, data: shaped, message: 'ok' };
};

// GCP-1 Deliverable #1 — shipped 2026-07-08. All 4 register variants funnel
// through admin-user-ops with the appropriate role_name. Only an existing
// platform_admin can invoke registerPlatformAdmin; backend enforces this.
export const registerPlatformAdmin = (userData = {}) =>
  registerAndAssign('register', {
    email: userData.email,
    password: userData.password,
    first_name: userData.first_name,
    last_name: userData.last_name,
    display_name: userData.display_name,
    org_id: userData.org_id || userData.company_id || null,
    role_name: 'platform_admin',
  }, 'register super admin');

export const registerAdmin = (userData = {}) =>
  registerAndAssign('register', {
    email: userData.email,
    password: userData.password,
    first_name: userData.first_name,
    last_name: userData.last_name,
    display_name: userData.display_name,
    org_id: userData.org_id || userData.company_id,
    role_name: 'org_admin',
  }, 'register admin');

export const registerManager = (userData = {}) =>
  registerAndAssign('register', {
    email: userData.email,
    password: userData.password,
    first_name: userData.first_name,
    last_name: userData.last_name,
    display_name: userData.display_name,
    org_id: userData.org_id || userData.company_id,
    role_name: 'org_analyst',
  }, 'register manager');

export const updateUser = async (userId, userData) => {
  // Email + password live in auth.users, NOT profiles. `fn_update_my_profile`
  // and `fn_admin_update_user_profile` don't touch auth.users — they silently
  // drop those fields. Route email/password through Supabase Auth explicitly:
  //   - self:  supabase.auth.updateUser({ email, password })
  //             (email change triggers Supabase's confirmation-email flow)
  //   - other: admin-user-ops Edge Function (needs service-role key)
  const { data: sess } = await supabase.auth.getSession();
  const isSelf = userId && sess?.session?.user?.id && String(userId) === String(sess.session.user.id);
  const currentEmail = sess?.session?.user?.email || null;

  if (isSelf) {
    // Auth updates first — if email confirmation fails, the profile row will
    // still get updated but the user knows the auth change is pending.
    const authPatch = {};
    if (userData.email && userData.email !== currentEmail) authPatch.email = userData.email;
    if (userData.password) authPatch.password = userData.password;
    if (Object.keys(authPatch).length) {
      const { error: authErr } = await supabase.auth.updateUser(authPatch);
      if (authErr) {
        // Common Supabase failure: SMTP not configured or built-in mailer
        // rate-limited → 500 "Error sending email change email". Give the
        // user actionable guidance instead of the raw AuthError.
        const raw = authErr.message || String(authErr);
        if (/email change email|unexpected_failure/i.test(raw) && authPatch.email) {
          throw {
            message:
              "Email couldn't be changed — Supabase Auth failed to send the confirmation email. " +
              "Ask an admin to configure SMTP in the Supabase project (Auth → SMTP), " +
              "or to run the change server-side via admin-user-ops.update_email.",
            code: 'supabase_mailer_failure',
            raw,
          };
        }
        throw { message: raw };
      }
    }
    const { data, error } = await supabase.rpc('fn_update_my_profile', {
      p_bio: userData.bio ?? null,
      p_display_name: userData.display_name ?? null,
      p_first_name: userData.first_name ?? null,
      p_last_name: userData.last_name ?? null,
      p_locale: userData.locale ?? null,
      p_navigation_preference: userData.navigation_preference ?? null,
      p_theme_preference: userData.theme_preference ?? null,
      p_timezone: userData.timezone ?? null,
    });
    if (error) throw { message: error.message };
    const message = authPatch.email
      ? 'Profile updated — check both email addresses to confirm the change'
      : 'Profile updated';
    return { error: false, data, message };
  }

  // Cross-user (platform_admin only). Route email/password to the Edge Function
  // (auth.users writes require service-role); profile columns to the admin RPC.
  // 2026-07-28 (Part 9): backend shipped update_email — route email changes
  // through that action, password-only through reset_password. No more
  // generic `update_user` guessing.
  if (userData.email) {
    await adminUserOps('update_email', { user_id: userId, email: userData.email }, 'update user email');
  }
  if (userData.password) {
    await adminUserOps('reset_password', { user_id: userId, password: userData.password }, 'reset password');
  }

  const { email, password, ...profileUpdates } = userData;
  void email; void password;
  if (Object.keys(profileUpdates).length) {
    const { data, error } = await supabase.rpc('fn_admin_update_user_profile', {
      p_user_id: userId,
      p_updates: profileUpdates,
    });
    if (error) throw { message: error.message };
    if (data?.error) throw { message: data.message };
    return { error: false, data, message: 'Profile updated' };
  }
  return { error: false, data: null, message: 'Auth credentials updated' };
};

// GCP-1 Deliverable #1 — shipped. delete_user via Edge Function; the function
// deactivates the profile, removes memberships, clears grants, then hard-
// deletes the auth.users row.
//
// PARTIAL-SUCCESS handling — origin story (Part 9): backend used to return
//   { error: true, message: "Auth delete failed: {}. Profile was deactivated." }
// because 13 auth.users rows from the July 16 direct-SQL migration had NULL
// token columns → GoTrue's scan blew up when deleteUser touched them. The bare
// `{}` was `err.toString()` on a non-standard error, and the "Profile was
// deactivated" half was itself a lie — the fallback status write violated the
// profiles CHECK constraint. All three fixed in Part 9: NULL tokens padded to
// '' (also fixed G6/listUsers), correct status='suspended' on soft-delete,
// and error serialization now falls through to real messages. This branch
// stays as a belt-and-suspenders guard against any surviving rows.
export const deleteUser = async (userId) => {
  try {
    return await adminUserOps('delete_user', { user_id: userId }, 'delete user');
  } catch (err) {
    const msg = err?.message || String(err);
    if (/profile was deactivated/i.test(msg)) {
      return {
        error: false,
        warning: true,
        data: null,
        message: 'Profile deactivated. Auth record cleanup pending — user is effectively removed but the login row still exists on the backend.',
        raw: msg,
      };
    }
    throw err;
  }
};

// GCP-1 shipped 2026-07-08 — backend added fn_admin_update_user_subscription.
// Consumer contract: pass { plan_id, seats, ... } as jsonb via p_updates.
export const updateUserSubscription = async (userId, subscriptionData) => {
  // The RPC's first arg is p_account_id (billing account), not user_id.
  // Legacy callers pass userId; if the account_id lives on the user's
  // profile/membership we forward it. Otherwise the RPC returns an error the
  // consumer can display.
  const { data, error } = await supabase.rpc('fn_admin_update_user_subscription', {
    p_account_id: subscriptionData.account_id || userId,
    p_updates: subscriptionData,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Subscription updated' };
};

// GCP-1 admin helpers — 2026-07-08 additions from the admin RPC catalog.
// Not previously in the legacy Cloud Run surface; expose so the new admin UIs
// can call them directly.

export const resetUserPassword = ({ userId, email, newPassword }) =>
  // Two modes:
  //   - { userId, newPassword } → admin sets a new password directly
  //   - { email }               → send a recovery email via Supabase Auth
  adminUserOps('reset_password', {
    user_id: userId,
    email,
    new_password: newPassword,
  }, 'reset password');

export const deactivateUser = async (userId) => {
  const { data, error } = await supabase.rpc('fn_admin_deactivate_user', { p_user_id: userId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'User deactivated' };
};

export const addUserToOrg = async (userId, orgId, roleName = 'org_viewer') => {
  const { data, error } = await supabase.rpc('fn_admin_add_user_to_org', {
    p_user_id: userId,
    p_org_id: orgId,
    p_role_name: roleName,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'User added to organization' };
};

// 2026-08-08 (backend brief §2): a role is a property of a MEMBERSHIP, not of
// a user — organization_members is unique on (org_id, user_id). Role changes
// go through fn_update_member_role (writes dashboard_audit_log; never update
// organization_members directly or the change goes unaudited). Takes the role
// UUID, not the name.
export const updateMemberRole = async (orgId, userId, roleId) => {
  const { data, error } = await supabase.rpc('fn_update_member_role', {
    p_org_id: orgId,
    p_user_id: userId,
    p_role_id: roleId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Role updated' };
};

// The five system roles are RLS-protected but readable ("Anyone can read
// system roles" policy, is_system = true). Read the table — never hardcode
// the list. Callers that render a customer-facing picker must filter out
// platform_admin themselves.
export const listOrgRoles = async () => {
  const { data, error } = await supabase
    .from('roles')
    .select('id, name, description, priority')
    .eq('is_system', true)
    .order('priority', { ascending: false });
  if (error) throw { message: error.message };
  return { error: false, data: data || [], message: 'ok' };
};

// Members of one org (fn_list_org_members). Used by the user-management list
// (Multi-Org Membership — Epic 2, S2.5) to show each user's role IN the selected
// org instead of a single flattened primary role. Returns rows with user_id,
// role_id, role_name, status, joined_at. SECURITY DEFINER; caller-scoped.
export const listOrgMembers = async (orgId) => {
  const safeId = safeUuid(orgId);
  if (!safeId) return { error: false, data: [], message: 'ok' };
  const { data, error } = await supabase.rpc('fn_list_org_members', { p_org_id: safeId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data: data || [], message: 'ok' };
};

export const listAdminUsers = async ({ orgId = null, page = 1, limit = 50, includeOrphans = true } = {}) => {
  // Part 9: both 3-arg and 4-arg overloads of fn_admin_list_users exist; passing
  // p_include_orphans disambiguates. Default TRUE — orphans are exactly who an
  // admin needs to find.
  const { data, error } = await supabase.rpc('fn_admin_list_users', {
    p_org_id: orgId,
    p_page: page,
    p_limit: limit,
    p_include_orphans: includeOrphans,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

export const listAdminRoles = async () => {
  const { data, error } = await supabase.rpc('fn_admin_list_roles');
  if (error) throw { message: error.message };
  return { error: false, data: data || [], message: 'ok' };
};

export const listClientAccess = async (clientId) => {
  const { data, error } = await supabase.rpc('fn_admin_list_client_access', { p_client_id: clientId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

export const listUserAccess = async (userId) => {
  const { data, error } = await supabase.rpc('fn_admin_list_user_access', { p_user_id: userId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

// ─── Epic 4 (2026-08-13) — Per-Property User Scoping admin actions ───────────
// fn_admin_scope_user_to_clients(user, org, client[]) is idempotent: re-run with
// a different array to change the subset. It refuses clients not in the org.
// "Selecting all" must NOT grant N rows — it restores org_viewer and clears
// grants, so the user resolves everything through the org (and auto-inherits
// any new property added later). See docs/PER-PROPERTY-USER-SCOPING-EPICS.md.
export const scopeUserToClients = async (userId, orgId, clientIds) => {
  const arr = Array.isArray(clientIds) ? clientIds : [];
  const { data, error } = await supabase.rpc('fn_admin_scope_user_to_clients', {
    p_user_id: userId,
    p_org_id: orgId,
    p_client_ids: arr,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'User scoped to selected properties' };
};

// Restore full org access: flip the membership back to org_viewer, then revoke
// every per-client grant for this user. grantedClientIds is the set of client
// ids the user currently reaches via grants (caller passes the Epic 3 reach
// ids). We loop fn_revoke_client_access (the clean per-client revoke RPC).
export const unscopeUser = async (userId, orgId, grantedClientIds) => {
  await addUserToOrg(userId, orgId, 'org_viewer');
  const failed = [];
  for (const cid of (grantedClientIds || [])) {
    const { error } = await supabase.rpc('fn_revoke_client_access', {
      p_user_id: userId,
      p_client_id: cid,
    });
    if (error) failed.push({ client_id: cid, error: error.message });
  }
  if (failed.length) throw { message: `Restored org_viewer but ${failed.length} grant(s) failed to revoke`, failed };
  return { error: false, data: { user_id: userId, org_id: orgId }, message: 'Restored full property access' };
};


// Project Management APIs
// GCP-2 Wave 1 — legacy `projectData.name` maps to `campground_name`; legacy
// `logoUrl`/`ai_enabled`/`type`/`company_id` fold back to native columns.
// Consumers writing legacy field names keep working.
//
// 2026-07-10 — direct Supabase probe (scripts/_probe-client-update.js) confirms
// the `clients` table has NO `features` / `feature_bookings` / `feature_google_ads` /
// `vf_project_id` / `vf_version_id` / `vf_api_key` / `group_name` / `description` /
// `supabase_client_id` / `client_display_name` columns. These are all Rigsby-era
// MySQL fossils. Per-client dashboard toggles live in `feature_flags` with
// scope='client', scope_id=<client_id> — write via `fn_admin_set_feature_flag`
// (or the new `setClientFeature` helper below), NOT via this mapper.
// Voiceflow credentials use `voiceflow_project_id_chats` / `voiceflow_api_key` /
// `voiceflow_project_id_calls` / `voiceflow_api_key_calls` / `twilio_phone_number`
// (handled by VoiceflowIntegrationSection, not this page's create/edit form).
const projectDataToClientRow = (d = {}) => {
  // 2026-07-15 — `clients.name` is NOT NULL. On create, the user types the
  // resort name; we mirror it into BOTH `name` (constraint) and
  // `campground_name` (display column used everywhere else). fn_admin_create_client
  // was failing 23502 because we only sent `campground_name`.
  const displayName = d.name ?? d.campground_name;
  return {
    name: displayName,
    campground_name: displayName,
    organization_id: d.company_id ?? d.organization_id,
    logo_url: d.logoUrl ?? d.logo_url,
    is_active: d.status === undefined ? undefined : d.status !== 0,
    // Chat/Voice product entitlement — the edit modal writes these explicitly
    // per Option A (2026-07-21). Legacy fallbacks kept for pre-Option-A callers
    // that still pass only `type` or `ai_enabled`.
    ai_chatbot_enabled: d.ai_chatbot_enabled ?? (d.type === 'voice' ? false : d.ai_enabled),
    ai_phone_agent_enabled: d.ai_phone_agent_enabled ?? (d.type === 'voice' ? true : undefined),
    reservation_system: d.reservation_system,
  };
};

// GCP-2 Deliverable (shipped 2026-07-08 via backend admin surface) — direct
// `.from('clients').insert/update()` was 42501 for authenticated users because
// `clients` has no policies (credentials-holding table). All writes now go
// through fn_admin_create_client / fn_admin_update_client which whitelist
// safe columns internally.
export const createProject = async (projectData) => {
  const row = Object.fromEntries(
    Object.entries(projectDataToClientRow(projectData)).filter(([, v]) => v !== undefined)
  );
  const { data, error } = await supabase.rpc('fn_admin_create_client', { p_data: row });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Client created' };
};

export const updateProject = async (projectId, projectData) => {
  const row = Object.fromEntries(
    Object.entries(projectDataToClientRow(projectData)).filter(([, v]) => v !== undefined)
  );
  const { data, error } = await supabase.rpc('fn_admin_update_client', {
    p_client_id: projectId,
    p_updates: row,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Client updated' };
};

// 2026-07-10 — per-client dashboard toggle. Writes a `feature_flags` row with
// scope='client', scope_id=<clientId>, flag_key=<dashboard.*|feature.*>.
// Direct upsert bypasses `fn_admin_set_feature_flag`'s config-NOT-NULL bug (B3).
export const setClientFeature = async (clientId, flagKey, enabled) => {
  const row = {
    flag_key: flagKey,
    scope: 'client',
    scope_id: clientId,
    enabled: !!enabled,
    config: {},
  };
  const direct = await supabase
    .from('feature_flags')
    .upsert(row, { onConflict: 'flag_key,scope,scope_id' })
    .select('id')
    .maybeSingle();
  if (!direct.error) return { error: false, data: direct.data, message: 'Feature flag saved' };
  // RLS blocked → fall through to the RPC (still buggy on config-NOT-NULL, may
  // fail; frontend surfaces the real error).
  const rpc = await supabase.rpc('fn_admin_set_feature_flag', {
    p_flag_key: flagKey,
    p_scope: 'client',
    p_scope_id: clientId,
    p_enabled: !!enabled,
    p_config: {},
  });
  if (rpc.error) throw { message: `Feature flag write blocked (direct: ${direct.error.message}; RPC: ${rpc.error.message})` };
  if (rpc.data?.error) throw { message: rpc.data.message };
  return { error: false, data: rpc.data, message: 'Feature flag saved' };
};

// Bulk convenience — takes {flagKey: bool, ...}, iterates setClientFeature per key.
export const setClientFeatures = async (clientId, featuresMap = {}) => {
  const entries = Object.entries(featuresMap);
  if (!entries.length) return { error: false, data: { updated: 0 }, message: 'No changes' };
  const failures = [];
  for (const [flagKey, enabled] of entries) {
    try { await setClientFeature(clientId, flagKey, enabled); }
    catch (e) { failures.push({ flagKey, error: e.message }); }
  }
  if (failures.length) throw { message: `${failures.length} of ${entries.length} flags failed`, failures };
  return { error: false, data: { updated: entries.length }, message: 'Client features updated' };
};

// Read helper — returns { flagKey: bool } resolved for the caller +
// specified client. Backend `fn_get_my_features(p_client_id)` does NOT apply
// scope='client' overrides for platform_admin (verified 2026-07-13 via browser
// console: fn_get_my_features returned only `dashboard.conversations: true`
// even though 5 scope='client' rows existed for Verde). So for platform_admin
// we bypass the resolver, fetch the full flag_flags via fn_admin_get_feature_flags
// (SECURITY DEFINER, returns rows across all scopes), and resolve locally.
// Scoped users (non-platform_admin) fall back to the RPC — they don't have
// admin access to the full registry anyway.
export const getClientFeatures = async (clientId) => {
  const adminRes = await supabase.rpc('fn_admin_get_feature_flags');
  if (!adminRes.error && !adminRes.data?.error) {
    const rows = Array.isArray(adminRes.data) ? adminRes.data : (adminRes.data?.flags || []);
    const resolved = {};
    for (const r of rows) if (r?.scope === 'global') resolved[r.flag_key] = !!r.enabled;
    for (const r of rows) if (r?.scope === 'client' && r.scope_id === clientId) resolved[r.flag_key] = !!r.enabled;
    return { error: false, data: { features: resolved, source: 'admin_registry' }, message: 'ok' };
  }
  // Non-platform_admin — fall back to fn_get_my_features (which respects
  // memberships but skips scope='client' overrides for callers without an
  // explicit grant). Better-than-nothing for org_viewer/analyst; they'll see
  // the scope='global' + scope='user'/'org' resolved values.
  const { data, error } = await supabase.rpc('fn_get_my_features', { p_client_id: clientId });
  if (error) throw { message: error.message };
  if (data && data.error) throw { message: data.message };
  const features = (data && typeof data === 'object' && !Array.isArray(data)) ? data : {};
  return { error: false, data: { features, source: 'fn_get_my_features' }, message: 'ok' };
};

// GCP-2 (new) — expose the full admin-only client record (all 130+ columns
// including credentials). Used by the Client Detail admin page's Integrations
// tab. Regular users still get the narrower `fn_get_my_clients` shape.
export const getProjectFullRecord = async (projectId) => {
  const { data, error } = await supabase.rpc('fn_admin_get_client', { p_client_id: projectId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'ok' };
};

export const searchSupabaseClients = async (q = '') => {
  // 2026-07-07 (v3): RLS on `clients` returns 42501. Filter client-side.
  const { data, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: error.message };
  const needle = (q || '').toLowerCase();
  let rows = (data || []);
  if (needle) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(needle));
  rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return { error: false, data: rows.slice(0, 50).map((r) => ({ id: r.id, name: r.name, organization_id: r.organization_id })), message: 'ok' };
};

export const deleteProject = async (projectId) => {
  // fn_admin_delete_client is the platform_admin path (soft-delete + audit).
  // Falls back to `soft_delete_client` (older RPC) if the admin one errors.
  const { data, error } = await supabase.rpc('fn_admin_delete_client', { p_client_id: projectId });
  if (!error && !data?.error) return { error: false, data, message: 'Client deleted' };
  const { error: fallbackErr } = await supabase.rpc('soft_delete_client', { client_uuid: projectId });
  if (fallbackErr) throw { message: fallbackErr.message };
  return { error: false, data: { id: projectId }, message: 'Client deleted' };
};

export const uploadProjectLogo = async (projectId, file) => {
  // Storage upload is unblocked; the DB row update was 42501-ing. Route the
  // logo_url write through fn_admin_update_client (whitelisted column).
  const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
  const path = `clients/${projectId}/${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage.from('logos').upload(path, file, { upsert: true, contentType: file.type || 'image/png' });
  if (upErr) throw { message: upErr.message };
  const { data: pub } = supabase.storage.from('logos').getPublicUrl(path);
  const logoUrl = pub?.publicUrl || null;
  const { data: rpcData, error: rpcErr } = await supabase.rpc('fn_admin_update_client', {
    p_client_id: projectId,
    p_updates: { logo_url: logoUrl },
  });
  if (rpcErr) throw { message: rpcErr.message };
  if (rpcData?.error) throw { message: rpcData.message };
  return { error: false, data: { logoUrl, logo_url: logoUrl }, message: 'Logo uploaded' };
};

// GCP-2 admin ops for client ↔ org relationships. All 3 wrapped from the
// backend's admin surface — no direct table writes.
export const assignClientToOrg = async (clientId, orgId) => {
  const { data, error } = await supabase.rpc('fn_admin_assign_client_to_org', {
    p_client_id: clientId, p_org_id: orgId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Client assigned to organization' };
};

export const unassignClientFromOrg = async (clientId) => {
  const { data, error } = await supabase.rpc('fn_admin_unassign_client_from_org', { p_client_id: clientId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Client unassigned' };
};

export const bulkAssignClientsToOrg = async (clientIds, orgId) => {
  const { data, error } = await supabase.rpc('fn_admin_bulk_assign_clients', {
    p_client_ids: clientIds, p_org_id: orgId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: `${data?.updated || 0} clients assigned` };
};

export const listUnlinkedClients = async () => {
  const { data, error } = await supabase.rpc('fn_admin_list_unlinked_clients');
  if (error) throw { message: error.message };
  return { error: false, data: data || [], message: 'ok' };
};

// =====================================================
// COMPANY MANAGEMENT APIs
// =====================================================

// GCP-2 Wave 2 — 2026-07-07. "Company" = "organization" in Supabase.
// Consumers may pass legacy `name/logoUrl/status`; we translate transparently.
const companyDataToOrgRow = (d = {}) => ({
  name: d.name,
  primary_contact_name: d.primary_contact_name ?? d.contact_name,
  primary_contact_email: d.primary_contact_email ?? d.contact_email,
  billing_contact_name: d.billing_contact_name,
  billing_contact_email: d.billing_contact_email,
  logo_url: d.logoUrl ?? d.logo_url,
  website: d.website,
  slug: d.slug,
  notes: d.notes,
  is_active: d.status === undefined ? undefined : d.status !== 0,
});

// GCP-2 org CRUD shipped 2026-07-08 via backend admin surface. All writes go
// through fn_admin_*_organization (SECURITY DEFINER) — RLS on `organizations`
// blocks direct writes for authenticated users.
export const createCompany = async (companyData) => {
  const row = companyDataToOrgRow(companyData);
  const { data, error } = await supabase.rpc('fn_admin_create_organization', {
    p_name: row.name,
    p_slug: row.slug || null,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Organization created' };
};

// 2026-07-07 (v3): `organizations` table returns 42501 for authenticated
// JWTs — same RLS gap as clients. Route through fn_get_my_organizations()
// (SECURITY DEFINER) which returns full org rows the caller is a member of.
//
// RPC output columns: id, name, slug, my_role, logo_url, is_active, public_id,
// client_count, member_count. Does NOT include: primary_contact_name/email,
// billing_contact_*, website, is_deleted (already filtered out), created_at.
// Missing columns fall back to null in the mapper.
//
// Known limitation: `fn_get_my_organizations()` returns only orgs the user
// is a member of. For a true "platform_admin sees all orgs" experience,
// backend must widen the RPC (auto-return all orgs when caller is
// platform_admin) OR add an authenticated SELECT policy on organizations.
// Documented in the master doc's P0 #2 extension.
const orgRpcToLegacyShape = (r) => ({
  id: r.id,
  name: r.name,
  primary_contact_name: null,
  primary_contact_email: null,
  logo_url: r.logo_url || null,
  is_active: r.is_active,
  // Legacy 0/1 status flag consumed by CompanyManagementPage's Active/Inactive
  // badge. Match the client mapper (line 210): only explicit `false` renders
  // as inactive; undefined/null/true → active.
  status: r.is_active === false ? 0 : 1,
  is_deleted: false,
  website: null,
  slug: r.slug || null,
  created_at: null,
  my_role: r.my_role || null,
  public_id: r.public_id || null,
  client_count: r.client_count ?? 0,
  member_count: r.member_count ?? 0,
});

// 2026-07-08 update: try fn_admin_list_organizations first (platform_admin
// gets all 150 orgs); on "Platform admin required" error, fall back to
// fn_get_my_organizations for scoped roles (org_owner/analyst/viewer see
// only their own memberships).
//
// 2026-09-25 (Q5, full-access staff): the Organizations admin page must NOT
// fall back. fn_admin_list_organizations refuses the six staff_full_access
// users with "Platform admin required"; the old fallback silently swapped in
// fn_get_my_organizations (which returns all 144 orgs for them because they
// are full-access), so they saw the admin org list anyway. Decision (Brian):
// the six get no admin screens, so the page now surfaces that error as a
// "platform admin required" state. Callers opt out of the fallback via
// { requireAdmin: true }. The Dashboard org dropdown (ViewContext) and the
// other surfaces keep the fallback so scoped roles still see their own orgs.
async function fetchOrgsForCaller({ requireAdmin = false } = {}) {
  const adminRes = await supabase.rpc('fn_admin_list_organizations');
  if (!adminRes.error && !adminRes.data?.error) return adminRes.data || [];
  // Admin-only callers surface the admin RPC's error (the Organizations admin
  // page renders its "platform admin required" state from it). Everyone else
  // falls back to their own memberships via fn_get_my_organizations.
  if (requireAdmin) {
    const msg = adminRes.data?.message || adminRes.error?.message || 'Platform admin required';
    throw { message: msg };
  }
  const scopedRes = await supabase.rpc('fn_get_my_organizations');
  if (scopedRes.error) throw { message: scopedRes.error.message };
  return scopedRes.data || [];
}

export const getAllCompanies = async ({ requireAdmin = false } = {}) => {
  const rows = await fetchOrgsForCaller({ requireAdmin });
  const shaped = rows.map(orgRpcToLegacyShape).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return { error: false, data: shaped, message: 'ok' };
};

export const getFilteredCompanies = async (page = 1, limit = 10, search = '', status = '') => {
  let rows = (await fetchOrgsForCaller()).map(orgRpcToLegacyShape);
  if (search) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(search.toLowerCase()));
  if (status !== '' && status !== null && status !== undefined) {
    const wantActive = Number(status) !== 0;
    rows = rows.filter((r) => !!r.is_active === wantActive);
  }
  rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const total = rows.length;
  const from = (page - 1) * limit;
  const paged = rows.slice(from, from + limit);
  return { error: false, data: paged, pagination: { page, limit, total }, message: 'ok' };
};

// Map Supabase role names to the legacy 0/1/2/3 role code the modal filter uses
// ("Only clients" = role === 3).
const ROLE_NAME_TO_CODE = {
  platform_admin: 0,
  org_owner: 1,
  org_admin: 1,
  org_analyst: 2,
  org_viewer: 3,
};

export const getCompanyDetails = async (companyId) => {
  // 2026-07-07 (v3): Both `.from('clients')` AND `.from('organizations')`
  // return 42501 for authenticated JWTs. Route through
  // fn_get_my_organizations + fn_get_my_clients — both SECURITY DEFINER.
  // 2026-07-27: also fetch members via fn_list_org_members so the detail
  // modal can render the Users tab (previously always 0 because company.users
  // was never populated).
  const safeId = safeUuid(companyId);
  if (!safeId) throw { message: 'Invalid organization id' };
  const [orgsRes, clientsRes, membersRes] = await Promise.all([
    supabase.rpc('fn_get_my_organizations'),
    supabase.rpc('fn_get_my_clients'),
    supabase.rpc('fn_list_org_members', { p_org_id: safeId }),
  ]);
  if (orgsRes.error) throw { message: orgsRes.error.message };
  const orgRow = (orgsRes.data || []).find((o) => o.id === safeId);
  if (!orgRow) throw { message: 'Organization not found' };

  const projects = (clientsRes.data || [])
    .filter((p) => p.organization_id === safeId)
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      logoUrl: null,
      status: p.is_active === false ? 0 : 1,
    }));

  const users = (membersRes.error ? [] : (membersRes.data || []))
    .filter((m) => !m.removed_at)
    .map((m) => ({
      id: m.user_id || m.membership_id,
      profile_id: m.user_id,
      email: m.email || '',
      first_name: m.first_name || '',
      last_name: m.last_name || '',
      display_name: m.display_name || [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email || '',
      avatar: m.avatar_url || null,
      role: ROLE_NAME_TO_CODE[m.role_name] ?? 3,
      role_name: m.role_name || null,
      status: m.status === 'active' ? 1 : 0,
      joined_at: m.joined_at || null,
      last_seen_at: m.last_seen_at || null,
    }));

  return {
    error: false,
    data: { ...orgRpcToLegacyShape(orgRow), projects, users },
    message: 'ok',
  };
};

export const updateCompany = async (companyId, companyData) => {
  const updates = Object.fromEntries(
    Object.entries(companyDataToOrgRow(companyData)).filter(([, v]) => v !== undefined)
  );
  const { data, error } = await supabase.rpc('fn_admin_update_organization', {
    p_org_id: companyId,
    p_updates: updates,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Organization updated' };
};

export const deleteCompany = async (companyId) => {
  const { data, error } = await supabase.rpc('fn_admin_delete_organization', { p_org_id: companyId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Organization deleted' };
};

export const addUserToCompany = async (companyId, userId, roleName = 'org_viewer') => {
  // 2026-07-08: backend shipped fn_admin_add_user_to_org — one call, no client fanout.
  const { data, error } = await supabase.rpc('fn_admin_add_user_to_org', {
    p_user_id: userId,
    p_org_id: companyId,
    p_role_name: roleName,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'User added to organization' };
};

export const removeUserFromCompany = async (companyId, userId) => {
  const { data, error } = await supabase.rpc('fn_admin_remove_user_from_org', {
    p_user_id: userId,
    p_org_id: companyId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'User removed from organization' };
};

export const addProjectToCompany = async (companyId, projectId) => {
  const { data, error } = await supabase.rpc('fn_admin_assign_client_to_org', {
    p_client_id: projectId,
    p_org_id: companyId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Client added to organization' };
};

export const removeProjectFromCompany = async (companyId, projectId) => {
  const { data, error } = await supabase.rpc('fn_admin_unassign_client_from_org', {
    p_client_id: projectId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Client removed from organization' };
};

export const batchAddUsersToCompany = async (companyId, userIds) => {
  const outcomes = [];
  for (const userId of userIds || []) {
    try {
      const r = await addUserToCompany(companyId, userId);
      outcomes.push({ user_id: userId, ok: true, ...r });
    } catch (e) {
      outcomes.push({ user_id: userId, ok: false, error: e.message });
    }
  }
  return { error: false, data: outcomes, message: 'Batch complete' };
};

export const batchAddProjectsToCompany = async (companyId, projectIds) => {
  if (!Array.isArray(projectIds) || !projectIds.length) return { error: false, data: [], message: 'No projects' };
  const { data, error } = await supabase.rpc('fn_admin_bulk_assign_clients', {
    p_client_ids: projectIds,
    p_org_id: companyId,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: `${projectIds.length} clients queued for assignment` };
};

export const uploadCompanyLogo = async (companyId, file) => {
  const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
  const path = `organizations/${companyId}/${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage.from('logos').upload(path, file, { upsert: true, contentType: file.type || 'image/png' });
  if (upErr) throw { message: upErr.message };
  const { data: pub } = supabase.storage.from('logos').getPublicUrl(path);
  const logoUrl = pub?.publicUrl || null;
  // 2026-07-08: writes to `organizations` go through fn_admin_update_organization.
  const { data: updData, error: updErr } = await supabase.rpc('fn_admin_update_organization', {
    p_org_id: companyId,
    p_updates: { logo_url: logoUrl },
  });
  if (updErr) throw { message: updErr.message };
  if (updData?.error) throw { message: updData.message };
  return { error: false, data: { logoUrl, logo_url: logoUrl }, message: 'Logo uploaded' };
};

// GCP-4 — 2026-07-07. Voiceflow evaluations don't have a Supabase home yet,
// so this export builds an Excel-like CSV from `conversations` + related
// `phone_call_transcripts` and returns a blob. Full parity with the legacy
// Voiceflow-integrated export needs a backend edge function joining
// conversation_analysis_runs; backlog until then.
export const exportTranscriptsWithEvaluations = async (projectId, startDate = null, endDate = null, limit = null, splitSize = null, includeMessages = true) => {
  void splitSize;
  const query = supabase
    .from('conversations')
    .select('id, kind, source, channel, started_at, ended_at, duration_seconds, message_count, visitor_name, visitor_email, tag, intent, sentiment, urgency, summary, has_booking, ai_tag_suggestion, ai_confidence, ai_tokens_used, ai_cost_cents, quality_score, status')
    .eq('client_id', projectId)
    .order('started_at', { ascending: false });
  if (startDate) query.gte('started_at', startDate);
  if (endDate) query.lte('started_at', endDate);
  if (limit) query.limit(Number(limit));
  const { data: convs, error } = await query;
  if (error) throw { message: error.message };
  let rows = convs || [];
  if (includeMessages && rows.length) {
    // Pull messages for each conversation. Batched for chunkiness.
    const ids = rows.map((r) => r.id);
    const { data: msgs } = await supabase
      .from('conversation_messages')
      .select('conversation_id, turn_order, role, content, created_at')
      .in('conversation_id', ids)
      .order('turn_order', { ascending: true });
    const byConv = new Map();
    (msgs || []).forEach((m) => {
      if (!byConv.has(m.conversation_id)) byConv.set(m.conversation_id, []);
      byConv.get(m.conversation_id).push(`${m.role}: ${m.content}`);
    });
    rows = rows.map((r) => ({ ...r, transcript: (byConv.get(r.id) || []).join('\n') }));
  }
  const headers = Object.keys(rows[0] || { id: '' });
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => CSV_ESCAPE(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  return blob;
};

// ========================= User-Project Assignment APIs =========================

/**
 * Get projects assigned to a specific user
 * @param {number} userId - User ID
 * @returns {Promise} - User's assigned projects with access levels
 */
// ─── GCP-3 — User-Project Access Grants (2026-07-07) ────────────────────
// Legacy semantics: users are granted 'view'/'edit'/'admin' access to
// individual projects. In Supabase-land: grants live in resource_grants /
// organization_members; the fn_grant_client_access / fn_revoke_client_access
// RPCs are the write path; fn_my_accessible_clients + fn_list_org_members
// provide the reads. Consumers pass legacy IDs and access strings; we
// translate transparently.
const ACCESS_DEFAULT = 'view';

export const getUserProjects = async (userId) => {
  void userId; // The RPC uses the CURRENT session's auth.uid(); cross-user
  // listing requires the caller's session already scoped to that user OR a
  // platform_admin call. platform_admin sees everything via RLS.
  const { data, error } = await supabase.rpc('fn_get_my_clients');
  if (error) throw { message: error.message };
  // 2026-07-28 — carry through the AI flags and branding so role=3 users can
  // gate their sidebar without a second admin-only enrichment fetch.
  const shaped = (data || []).map((r) => ({
    id: r.id || r.client_id,
    projectId: r.id || r.client_id,
    name: r.campground_name || r.name || r.client_name || '',
    campground_name: r.campground_name || null,
    logo_url: r.logo_url || null,
    logoUrl: r.logo_url || null,
    access_type: r.permission || 'view',
    company_id: r.organization_id || null,
    organization_id: r.organization_id || null,
    ai_chatbot_enabled: r.ai_chatbot_enabled ?? null,
    ai_phone_agent_enabled: r.ai_phone_agent_enabled ?? null,
    ai_phone_after_hours_only: r.ai_phone_after_hours_only ?? null,
    ai_enabled: !!(r.ai_chatbot_enabled || r.ai_phone_agent_enabled),
    type: r.ai_phone_agent_enabled ? 'voice' : 'chat',
    is_active: r.is_active !== false,
  }));
  return { error: false, data: shaped, message: 'ok' };
};

export const getProjectUsers = async (projectId) => {
  // No RPC returns "users granted to a client" directly, but we can filter
  // organization_members by the client's parent org, then intersect with
  // resource_grants where resource_id = projectId. Simplified: read the
  // client's organization_members via a join.
  const { data: client, error: cErr } = await supabase
    .from('clients')
    .select('id, organization_id')
    .eq('id', projectId)
    .maybeSingle();
  if (cErr) throw { message: cErr.message };
  if (!client?.organization_id) return { error: false, data: [], message: 'no org' };
  const { data: members, error: mErr } = await supabase.rpc('fn_list_org_members', { p_org_id: client.organization_id });
  if (mErr) throw { message: mErr.message };
  return { error: false, data: members || [], message: 'ok' };
};

export const assignProjectsToUser = async (userId, projectIds, accessType = ACCESS_DEFAULT, notes = null) => {
  void notes; // fn_grant_client_access doesn't accept notes; drop or persist
  // separately (backlog if audit is important).
  const outcomes = [];
  for (const projectId of projectIds || []) {
    const { error } = await supabase.rpc('fn_grant_client_access', {
      p_user_id: userId,
      p_client_id: projectId,
      p_permission: accessType,
    });
    outcomes.push({ project_id: projectId, ok: !error, error: error?.message });
  }
  const failed = outcomes.filter((o) => !o.ok);
  if (failed.length) throw { message: `${failed.length} of ${outcomes.length} grants failed`, outcomes };
  return { error: false, data: outcomes, message: 'Projects assigned' };
};

export const assignUsersToProject = async (projectId, userIds, accessType = ACCESS_DEFAULT, notes = null) => {
  void notes;
  const outcomes = [];
  for (const userId of userIds || []) {
    const { error } = await supabase.rpc('fn_grant_client_access', {
      p_user_id: userId,
      p_client_id: projectId,
      p_permission: accessType,
    });
    outcomes.push({ user_id: userId, ok: !error, error: error?.message });
  }
  const failed = outcomes.filter((o) => !o.ok);
  if (failed.length) throw { message: `${failed.length} of ${outcomes.length} grants failed`, outcomes };
  return { error: false, data: outcomes, message: 'Users assigned' };
};

export const updateProjectAccess = async (assignmentId, accessType, notes = null) => {
  // `assignmentId` was the resource_grants row id in the legacy schema.
  // Direct update via table (RLS enforces admin-only).
  void notes;
  const { data, error } = await supabase
    .from('resource_grants')
    .update({ permission_key: accessType })
    .eq('id', assignmentId)
    .select()
    .maybeSingle();
  if (error) throw { message: error.message };
  return { error: false, data, message: 'Access updated' };
};

export const removeProjectAccess = async (assignmentId) => {
  const { error } = await supabase.from('resource_grants').delete().eq('id', assignmentId);
  if (error) throw { message: error.message };
  return { error: false, data: { id: assignmentId }, message: 'Access removed' };
};

export const removeProjectsFromUser = async (userId, projectIds) => {
  const outcomes = [];
  for (const projectId of projectIds || []) {
    const { error } = await supabase.rpc('fn_revoke_client_access', {
      p_user_id: userId,
      p_client_id: projectId,
      p_permission: ACCESS_DEFAULT,
    });
    outcomes.push({ project_id: projectId, ok: !error, error: error?.message });
  }
  return { error: false, data: outcomes, message: 'Projects removed' };
};

// 2026-07-07: RLS policy on `organization_members` recurses into itself
// (42P17). Direct `.from('organization_members')` selects fail from the
// browser. Use SECURITY DEFINER RPCs — fn_get_my_organizations to enumerate
// orgs, fn_list_org_members to read members inside each org.
export const getUserAccessMode = async (userId) => {
  const { data: orgs, error: orgErr } = await supabase.rpc('fn_get_my_organizations');
  if (orgErr) throw { message: orgErr.message };
  const orgIds = (orgs || []).map((o) => o.id || o.org_id).filter(Boolean);
  const memberships = [];
  for (const orgId of orgIds) {
    const { data } = await supabase.rpc('fn_list_org_members', { p_org_id: orgId });
    (data || []).forEach((m) => { if (m.user_id === userId) memberships.push({ ...m, org_id: orgId }); });
  }
  // Role priority: platform_admin=1000, org_owner=800, org_admin=600,
  // org_analyst=400, org_viewer=200. Higher priority = broader access.
  const ROLE_PRIORITY = { platform_admin: 1000, org_owner: 800, org_admin: 600, org_analyst: 400, org_viewer: 200 };
  const highestPriority = memberships.reduce((max, m) => Math.max(max, ROLE_PRIORITY[m.role_name] || 0), 0);
  return {
    error: false,
    data: {
      mode: highestPriority >= 500 ? 'company_fallback' : 'explicit',
      role_priority: highestPriority,
      memberships,
    },
    message: 'ok',
  };
};

export const getAvailableProjectsForUser = async (userId) => {
  const { data: orgs, error: orgErr } = await supabase.rpc('fn_get_my_organizations');
  if (orgErr) throw { message: orgErr.message };
  // Filter to the orgs where the target userId is a member.
  const memberOrgIds = [];
  for (const o of orgs || []) {
    const orgId = o.id || o.org_id;
    const { data } = await supabase.rpc('fn_list_org_members', { p_org_id: orgId });
    if ((data || []).some((m) => m.user_id === userId)) memberOrgIds.push(orgId);
  }
  const orgIds = memberOrgIds;
  if (!orgIds.length) return { error: false, data: [], message: 'no memberships' };
  const { data: clients, error: cErr } = await supabase
    .from('clients')
    .select('id, campground_name, name, organization_id')
    .in('organization_id', orgIds)
    .eq('is_deleted', false)
    .order('campground_name', { ascending: true });
  if (cErr) throw { message: cErr.message };
  const shaped = (clients || []).map((r) => ({
    id: r.id,
    name: r.campground_name || r.name,
    company_id: r.organization_id,
  }));
  return { error: false, data: shaped, message: 'ok' };
};

// Profile & Settings Functions
// GCP-1 Wave A — Supabase Auth doesn't verify the current password when
// changing (it trusts the active session), so `currentPassword` is now
// advisory. If the app wants stronger UX, re-authenticate the user by
// calling signInWithPassword({email, currentPassword}) BEFORE this — that
// verifies the current password and refreshes the session in one shot.
export const changePassword = async (currentPassword, newPassword) => {
  void currentPassword;
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw { message: error.message };
  return { error: false, data, message: 'Password updated' };
};

// GCP-1 Wave C — 2026-07-06. Public `avatars` bucket exists. Path is
// `<user_id>/<timestamp>.<ext>` so RLS can scope updates per-user (policy:
// name LIKE auth.uid()::text || '/%'). Consumer reads .data.avatar so we
// return the public URL there.
export const uploadAvatar = async (file) => {
  const { data: sess } = await supabase.auth.getSession();
  const userId = sess?.session?.user?.id;
  if (!userId) throw { message: 'Not authenticated' };
  const ext = (file.name?.split('.').pop() || 'png').toLowerCase();
  const path = `${userId}/${Date.now()}.${ext}`;
  const { error: upErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true, contentType: file.type || 'image/png' });
  if (upErr) throw { message: upErr.message };
  const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
  const avatarUrl = pub?.publicUrl || null;
  // Persist on the user's profile so it survives across sessions/devices.
  const { error: profErr } = await supabase.from('profiles').update({ avatar_url: avatarUrl }).eq('owned_by_user_id', userId);
  if (profErr) throw { message: profErr.message };
  return { error: false, data: { avatar: avatarUrl }, message: 'Avatar uploaded' };
};

export const removeAvatar = async () => {
  const { data: sess } = await supabase.auth.getSession();
  const userId = sess?.session?.user?.id;
  if (!userId) throw { message: 'Not authenticated' };
  // List and delete every object under the user's folder — cheap since the
  // folder only holds one active avatar at a time (upsert semantics).
  const { data: files } = await supabase.storage.from('avatars').list(userId);
  if (files?.length) {
    const paths = files.map(f => `${userId}/${f.name}`);
    await supabase.storage.from('avatars').remove(paths);
  }
  const { error: profErr } = await supabase.from('profiles').update({ avatar_url: null }).eq('owned_by_user_id', userId);
  if (profErr) throw { message: profErr.message };
  return { error: false, data: { avatar: null }, message: 'Avatar removed' };
};

// GCP-1 leftover — 2026-07-07. Theme lives on `profiles.theme_preference`;
// fn_update_my_profile is the write path (accepts p_theme_preference).
//
// D1 (read-back-for-certainty, 2026-09-15): fn_update_my_profile returns
// { success: true } UNCONDITIONALLY and does not report whether a row was
// updated — a caller with no `profiles` row gets a green save that did nothing
// (the exact "saved but never read back" bug class). So after the write we read
// the value back via fn_get_my_profile and compare. The result carries
// `confirmed` (bool) and `warning` (string|null) so callers can surface a
// warning on a silent no-op instead of trusting the write. A read-back failure
// is soft — the write itself didn't error, so we don't fail the whole save, we
// just mark it unconfirmed. See docs/THEME-PREFERENCE-EPICS.md → Decision D1.
export const updateThemePreference = async (theme) => {
  const { data, error } = await supabase.rpc('fn_update_my_profile', {
    p_bio: null,
    p_display_name: null,
    p_first_name: null,
    p_last_name: null,
    p_locale: null,
    p_navigation_preference: null,
    p_theme_preference: theme,
    p_timezone: null,
  });
  if (error) throw { message: error.message };

  // Read back to confirm the value actually landed. fn_update_my_profile does
  // not report a row count, so this is the only way to distinguish a real save
  // from a silent no-op (e.g. a user with no `profiles` row).
  let confirmed = false;
  let warning = null;
  try {
    const { data: profile, error: readErr } = await supabase.rpc('fn_get_my_profile');
    if (readErr) {
      warning = `Theme saved on this device, but the server couldn't confirm it (${readErr.message}). It may not persist on reload.`;
    } else {
      const p = Array.isArray(profile) ? profile[0] : profile || {};
      if (p.theme_preference === theme) {
        confirmed = true;
      } else {
        warning = `Theme saved on this device, but the server still reports "${p.theme_preference ?? 'none'}". It may not persist on reload.`;
      }
    }
  } catch (e) {
    warning = `Theme saved on this device, but the server confirmation read failed (${e?.message || 'unknown error'}). It may not persist on reload.`;
  }

  return { error: false, data, message: 'Theme updated', confirmed, warning };
};

// GCP-4 — 2026-07-07. audit_log has 10 columns and is the source of truth
// for change history. Consumers page/filter by action (insert/update/delete).
export const getActivityLog = async (page = 1, limit = 20, action = null) => {
  const from = (page - 1) * limit;
  const to = from + limit - 1;
  let query = supabase
    .from('audit_log')
    .select('id, table_name, record_id, action, old_values, new_values, changed_fields, changed_by, change_source, created_at', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);
  if (action) query = query.eq('action', action);
  const { data, count, error } = await query;
  if (error) throw { message: error.message };
  return { error: false, data: data || [], pagination: { page, limit, total: count }, message: 'ok' };
};

export const getUserActivityLog = async (userId, page = 1, limit = 20, action = null) => {
  const from = (page - 1) * limit;
  const to = from + limit - 1;
  let query = supabase
    .from('audit_log')
    .select('id, table_name, record_id, action, old_values, new_values, changed_fields, changed_by, change_source, created_at', { count: 'exact' })
    .eq('changed_by', userId)
    .order('created_at', { ascending: false })
    .range(from, to);
  if (action) query = query.eq('action', action);
  const { data, count, error } = await query;
  if (error) throw { message: error.message };
  return { error: false, data: data || [], pagination: { page, limit, total: count }, message: 'ok' };
};

// ─── GCP-5 — Upsell Tracking & Notifications (2026-07-07) ───────────────
// Backed by the generic `notifications` table (13 cols: kind, title, body,
// payload jsonb, action_url, read_at, dismissed_at, user_id, org_id, ...).
// All upsell rows use kind='upsell' + payload={product, action, project_id, notes}.

const UPSELL_KIND = 'upsell';

export const trackUpsellInteraction = async (action, product, project_id = null, notes = null) => {
  const { data: sess } = await supabase.auth.getSession();
  const userId = sess?.session?.user?.id;
  const { data, error } = await supabase
    .from('notifications')
    .insert({
      user_id: userId,
      kind: UPSELL_KIND,
      title: `${product}: ${action}`,
      body: notes || null,
      payload: { product, action, project_id, notes },
      source_type: 'upsell',
      source_id: project_id ? String(project_id) : null,
    })
    .select()
    .single();
  if (error) throw { message: error.message };
  return { error: false, data, message: 'Interaction tracked' };
};

export const getUpsellInteractions = async (params = {}) => {
  const page = Number(params.page || 1);
  const limit = Number(params.limit || 20);
  const from = (page - 1) * limit;
  const to = from + limit - 1;
  let query = supabase
    .from('notifications')
    .select('id, user_id, kind, title, body, payload, action_url, source_type, source_id, read_at, dismissed_at, created_at', { count: 'exact' })
    .eq('kind', UPSELL_KIND)
    .is('dismissed_at', null)
    .order('created_at', { ascending: false })
    .range(from, to);
  if (params.product) query = query.contains('payload', { product: params.product });
  if (params.action) query = query.contains('payload', { action: params.action });
  const { data, count, error } = await query;
  if (error) throw { message: error.message };
  return { error: false, data: data || [], pagination: { page, limit, total: count }, message: 'ok' };
};

export const getUnreadUpsellCount = async () => {
  const { data: sess } = await supabase.auth.getSession();
  const userId = sess?.session?.user?.id;
  let query = supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('kind', UPSELL_KIND)
    .is('read_at', null)
    .is('dismissed_at', null);
  if (userId) query = query.eq('user_id', userId);
  const { count, error } = await query;
  if (error) throw { message: error.message };
  return { error: false, data: { unread: count || 0 }, message: 'ok' };
};

export const markUpsellAsRead = async (ids) => {
  const list = Array.isArray(ids) ? ids : [ids];
  const { data, error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .in('id', list)
    .eq('kind', UPSELL_KIND)
    .select('id');
  if (error) throw { message: error.message };
  return { error: false, data: { updated: (data || []).length }, message: 'Marked as read' };
};

export const markAllUpsellAsRead = async () => {
  const { data: sess } = await supabase.auth.getSession();
  const userId = sess?.session?.user?.id;
  let query = supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('kind', UPSELL_KIND)
    .is('read_at', null);
  if (userId) query = query.eq('user_id', userId);
  const { data, error } = await query.select('id');
  if (error) throw { message: error.message };
  return { error: false, data: { updated: (data || []).length }, message: 'All marked as read' };
};

export const getUpsellStatistics = async (start_date = null, end_date = null) => {
  let query = supabase
    .from('notifications')
    .select('payload, read_at, dismissed_at, created_at')
    .eq('kind', UPSELL_KIND);
  if (start_date) query = query.gte('created_at', start_date);
  if (end_date) query = query.lte('created_at', end_date);
  const { data, error } = await query;
  if (error) throw { message: error.message };
  const rows = data || [];
  const byAction = {}, byProduct = {};
  let read = 0, dismissed = 0;
  rows.forEach((r) => {
    const a = r.payload?.action || 'unknown';
    const p = r.payload?.product || 'unknown';
    byAction[a] = (byAction[a] || 0) + 1;
    byProduct[p] = (byProduct[p] || 0) + 1;
    if (r.read_at) read++;
    if (r.dismissed_at) dismissed++;
  });
  return {
    error: false,
    data: {
      total: rows.length,
      read,
      dismissed,
      by_action: byAction,
      by_product: byProduct,
    },
    message: 'ok',
  };
};

export const deleteUpsellInteraction = async (id) => {
  // Soft-delete via dismissed_at so aggregate stats stay accurate. Consumer
  // filters out dismissed rows in list views.
  const { error } = await supabase
    .from('notifications')
    .update({ dismissed_at: new Date().toISOString() })
    .eq('id', id)
    .eq('kind', UPSELL_KIND);
  if (error) throw { message: error.message };
  return { error: false, data: { id }, message: 'Interaction dismissed' };
};

// =============================================================================
// TRANSCRIPT SYNC API
// =============================================================================

/**
 * Update transcript sync configuration for a project
 */
// ─── GCP-7 — Sync Engine (2026-07-08) ────────────────────────────────────
// 2026-07-08: Deliverable #5 shipped. Backend deployed the sync management
// surface: fn_get_sync_state / fn_update_sync_config / fn_manual_sync /
// fn_batch_sync_all (RPCs) + `sync-trigger` Edge Function (cron-invoked, but
// also invocable manually from admin UI to force an immediate cycle).

export const updateSyncConfig = async (syncId, updates) => {
  const { data, error } = await supabase.rpc('fn_update_sync_config', {
    p_sync_id: syncId,
    p_updates: updates,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Sync config updated' };
};

export const manualSync = async (syncId) => {
  const { data, error } = await supabase.rpc('fn_manual_sync', { p_sync_id: syncId });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: data?.message || 'Sync queued' };
};

export const batchSyncAll = async () => {
  const { data, error } = await supabase.rpc('fn_batch_sync_all');
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: `${data?.queued ?? 0} sync(s) queued` };
};

// 2026-07-08: Admin UI can hit sync-trigger directly for an immediate run.
export const triggerSyncNow = async () => {
  const { data, error } = await supabase.functions.invoke('sync-trigger');
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Sync trigger fired' };
};

// 2026-07-08: fn_get_sync_state returns the full pipeline roster (source,
// entity, last_sync_at, last_sync_count, total_synced, is_enabled,
// sync_interval_minutes, consecutive_errors, last_error_message, config).
// Falls back to the raw ops_sync_state read if the RPC isn't available
// (older environments).
export const getSyncStatus = async (projectId) => {
  const { data: rpcData, error: rpcErr } = await supabase.rpc('fn_get_sync_state', { p_client_id: projectId || null });
  if (!rpcErr && !rpcData?.error) {
    const rows = Array.isArray(rpcData) ? rpcData : (rpcData?.states || []);
    return { error: false, data: { states: rows, latest: rows[0] || null }, message: 'ok' };
  }
  let query = supabase
    .from('ops_sync_state')
    .select('*')
    .order('updated_at', { ascending: false })
    .limit(20);
  if (projectId) query = query.eq('client_id', projectId);
  const { data, error } = await query;
  if (error) throw { message: error.message };
  return { error: false, data: { states: data || [], latest: data?.[0] || null }, message: 'ok' };
};

export const getSyncHistory = async (projectId, limit = 20) => {
  // extraction_logs is the append-only history table. Filter by client
  // where available; return latest rows first.
  let query = supabase
    .from('extraction_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
  if (projectId) query = query.eq('client_id', projectId);
  const { data, error } = await query;
  if (error) throw { message: error.message };
  return { error: false, data: data || [], message: 'ok' };
};

// =====================================================
// BOOKING / CUSTOMER DASHBOARD APIs
// =====================================================

export const getBookingKpis = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_booking_kpis', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingFunnel = async (clientId, startDate, endDate) => {
  // Migrated 2026-07-01 per backend "8 held helpers" doc (2026-06-21). New RPC
  // returns 6 stages (was 8) with keys `stages`/`stage`, adds `worst_drop_off_index`,
  // `unmapped_sessions`, `unmapped_steps`. Booking count uses the corrected
  // transaction-deduplicated definition (~1,300+ for Verde, was 198). Consumers
  // reading `.steps` / `.step` need to update to `.stages` / `.stage`.
  const { data, error } = await supabase.rpc('fn_get_booking_funnel', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingSessions = async (clientId, startDate, endDate, params = {}) => {
  // Map legacy query params → RPC params. Only forward the ones the RPC
  // accepts; anything else is silently dropped so old call sites keep working.
  const rpcParams = {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  };
  const aliases = { page: 'p_page', limit: 'p_limit', outcome: 'p_outcome', site_category: 'p_site_category', search: 'p_search' };
  for (const [src, dst] of Object.entries(aliases)) {
    if (params[src] !== undefined && params[src] !== null && params[src] !== '') rpcParams[dst] = params[src];
  }
  if (rpcParams.p_page === undefined) rpcParams.p_page = 1;
  if (rpcParams.p_limit === undefined) rpcParams.p_limit = 25;
  const { data, error } = await supabase.rpc('fn_get_booking_sessions', rpcParams);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingSessionDetail = async (id) => {
  // Migrated 2026-07-03. Part 4 audit had marked this as "backend claim
  // overstated" — the audit probed (p_client_id, p_session_id) and got PGRST202.
  // Reality: the RPC exists with a single-arg signature (p_session_id uuid).
  // Discovered by scanning /rest/v1 OpenAPI spec (all 317 deployed RPCs).
  // Returns 77 session fields + { person, timeline, suppression, web_sessions,
  // visitor_history, recovery_actions } sub-objects — the full contract from
  // backend doc §2 of the 2026-06-21 "8 held helpers" update.
  const { data, error } = await supabase.rpc('fn_get_session_detail', { p_session_id: id });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingTrends = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_booking_trends', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingInsights = async (clientId, startDate, endDate) => {
  // Migrated 2026-07-01. New RPC output contract: site_types items use `type`
  // + `conversion_pct`; devices under `devices` (was `device`); referral items
  // use `mentions`. Bookings deduped by transaction_id (accurate count now).
  const { data, error } = await supabase.rpc('fn_get_booking_insights', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingDemandCalendar = async (clientId, startDate, endDate, lookaheadDays = 90) => {
  const { data, error } = await supabase.rpc('fn_get_demand_calendar', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_lookahead_days: lookaheadDays,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingWindow = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_booking_window', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingPriceSensitivity = async (clientId, startDate, endDate) => {
  // Migrated 2026-07-01. New shape: 7 fine buckets keyed `price_bucket` (was 5
  // coarse buckets keyed `bucket`). Elasticity peak at $75-99 for Verde; $100+
  // drops sharply. Deduped bookings via shared fn_funnel_stage() gate.
  const { data, error } = await supabase.rpc('fn_get_price_sensitivity', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingCrossBrowse = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_cross_browse', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingDayOfWeek = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_day_of_week', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  // Adapter: Supabase RPC returns { matrix: [{ search_dow, stay_dow, count }] }.
  // Legacy Express returned { headline_pct, matrix: [{ search_dow, checkin_dow, sessions }] }.
  // Rename fields and compute headline_pct = % of sessions where guests
  // searched on the same DOW they want to check in.
  const rawMatrix = Array.isArray(data?.matrix) ? data.matrix : [];
  const matrix = rawMatrix.map((row) => ({
    search_dow: row.search_dow,
    checkin_dow: row.stay_dow ?? row.checkin_dow,
    sessions: row.count ?? row.sessions ?? 0,
  }));
  const total = matrix.reduce((acc, r) => acc + (r.sessions || 0), 0);
  const sameDow = matrix.filter((r) => r.search_dow === r.checkin_dow)
    .reduce((acc, r) => acc + (r.sessions || 0), 0);
  const headline_pct = total > 0 ? Math.round((sameDow / total) * 100) : 0;
  return wrapSupabase({ headline_pct, matrix });
};

export const getBookingRvIntelligence = async (clientId, startDate, endDate) => {
  // Migrated 2026-07-01. Was 22P02 in Cloud Run (bad int cast on categorical
  // text). New RPC groups slide-outs as a text position category — each item
  // is `{position, sessions, bookings}` (text label, not int count).
  // Bookings per rv_type are ~7× higher than Cloud Run — intended per doc §1
  // (transaction-deduplicated definition supersedes the broken 16% flag).
  // Localized values (Driver Side / Lado do motorista / Bestuurderskant / なし)
  // still surface as separate buckets — normalization is a follow-up.
  const { data, error } = await supabase.rpc('fn_get_rv_intelligence', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingPetDemand = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_pet_demand', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingAbandonedCarts = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_abandoned_carts', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getBookingClients = async () => {
  const { data, error } = await supabase.rpc('fn_get_booking_clients');
  if (error) wrapSupabaseError(error);
  // Adapter: Supabase returns { id, name, city, state, ... }.
  // Legacy Express returned { id, campground_name }. Alias `name` so
  // existing call sites (e.g. ALL_CLIENTS_MAP) keep working.
  const rows = Array.isArray(data) ? data : [];
  const aliased = rows.map((row) => ({
    ...row,
    campground_name: row.name ?? row.campground_name ?? null,
  }));
  return wrapSupabase(aliased);
};

// =====================================================
// CART EMAIL RECOVERY APIs
// =====================================================

// ─── GCP-6 — Cart Recovery Email (2026-07-07) ───────────────────────────
// generate/send route through the `cv-recovery` Supabase Edge Function
// (existed since Rigsby era per project memory). The Edge Function handles
// AI generation via Gemini + Postmark send. Status reads directly from
// recovery_actions table.
//
// If the Edge Function's action names differ from ('generate','send') the
// backend team's contract, we surface the underlying error message so the
// frontend can update the payload shape without another migration pass.

export const generateCartEmail = async (session_id, client_id, custom_instructions = '') => {
  const { data, error } = await supabase.functions.invoke('cv-recovery', {
    body: { action: 'generate', session_id, client_id, custom_instructions },
  });
  if (error) throw { message: error.message || 'cv-recovery edge function generate failed' };
  return data;
};

export const sendCartEmail = async ({ recovery_action_id, subject, body_html }) => {
  const { data, error } = await supabase.functions.invoke('cv-recovery', {
    body: { action: 'send', recovery_action_id, subject, body_html },
  });
  if (error) throw { message: error.message || 'cv-recovery edge function send failed' };
  return data;
};

export const getCartEmailStatus = async (clientId, startDate, endDate) => {
  // 2026-08-13 — recovery_actions has RLS enabled (42501 permission denied for
  // authenticated users), so a direct .from('recovery_actions') read fails
  // silently in the browser. Route through fn_get_recovery_activity instead
  // (SECURITY DEFINER, bypasses RLS). That RPC returns rows keyed by to_email
  // (not by session_id), so we build the map keyed by lowercased email and the
  // caller looks up by cart.visitor_email instead of cart.id.
  //
  // The RPC caps at ~100 rows per page, so we page through to cover the full
  // date range. Most properties have <100 recovery emails per 30d window.
  const statuses = {};
  let page = 1;
  const limit = 100;
  let gotAll = false;
  while (!gotAll) {
    const { data, error } = await supabase.rpc('fn_get_recovery_activity', {
      p_client_id: clientId,
      p_start_date: startDate,
      p_end_date: endDate,
      p_page: page,
      p_limit: limit,
    });
    if (error) throw { message: error.message };
    const rows = data?.activity || data?.actions || (Array.isArray(data) ? data : []);
    if (!Array.isArray(rows) || rows.length === 0) { gotAll = true; break; }
    for (const r of rows) {
      const email = (r.to_email || '').toLowerCase().trim();
      if (email && !statuses[email]) {
        // First (most recent, since ordered desc) row per email wins.
        statuses[email] = {
          status: r.status,
          sent_at: r.sent_at || r.created_at || r.delivered_at,
          opened_at: r.opened_at,
          clicked_at: r.clicked_at,
          converted_at: r.converted_at,
          revenue_recovered: r.converted_value,
          subject: r.subject,
        };
      }
    }
    if (rows.length < limit) gotAll = true;
    else page++;
    if (page > 10) gotAll = true; // safety cap (1000 rows)
  }
  return { error: false, data: { statuses }, message: 'ok' };
};

// =====================================================
// RECOVERY STATISTICS APIs
// =====================================================

export const getRecoveryStats = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_recovery_stats', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  // All counts (total_sent, delivered, opened, clicked, converted, bounced,
  // revenue_recovered, cart_value_targeted, conversion_rate, revenue_per_send)
  // are value-identical to the legacy Express endpoint (verified 2026-06-19).
  // The RPC computes open_rate/click_rate against total_sent, but the dashboard
  // has always shown open_rate = opened/delivered and click_rate = clicked/opened
  // (click-to-open). Recompute both to preserve the displayed numbers exactly.
  // NOTE: backend has flagged standardizing the denominator — if we adopt the
  // RPC's total_sent basis, delete this recompute block and return data as-is.
  const d = (data && typeof data === 'object' && !Array.isArray(data))
    ? data : ((Array.isArray(data) ? data[0] : null) || {});
  const delivered = Number(d.delivered) || 0;
  const opened = Number(d.opened) || 0;
  const clicked = Number(d.clicked) || 0;
  const open_rate = delivered > 0 ? Math.round((opened / delivered) * 1000) / 10 : 0;
  const click_rate = opened > 0 ? Math.round((clicked / opened) * 1000) / 10 : 0;
  return wrapSupabase({ ...d, open_rate, click_rate });
};

export const getRecoveryTrends = async (clientId, startDate, endDate) => {
  // Migrated to fn_get_recovery_weekly_trends (a new RPC — the doc originally
  // pointed at v_recovery_summary, which is per-campaign cumulative, not the
  // weekly time-series the chart needs). All weeks value-verified 2026-06-19.
  const { data, error } = await supabase.rpc('fn_get_recovery_weekly_trends', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getRecoverySequences = async (clientId, startDate, endDate) => {
  // Migrated to fn_get_recovery_sequences_with_stats (a new RPC — the doc
  // originally pointed at the recovery_sequences table, which holds config
  // only, no per-sequence aggregates). Value-verified 2026-06-19.
  const { data, error } = await supabase.rpc('fn_get_recovery_sequences_with_stats', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getRecoveryActivity = async (clientId, page = 1, limit = 20, startDate, endDate) => {
  // Migrated 2026-07-01. Cloud Run `/recovery/activity` returns HTTP 404 —
  // Express route was never wired; Supabase RPC is the only path. `total`
  // counts rows with `sent_at IS NOT NULL` (334 all-time on Verde; 409 total
  // actions − 52 failed − 23 pending). Optional date window: pass null/null
  // for all-time.
  const { data, error } = await supabase.rpc('fn_get_recovery_activity', {
    p_client_id: clientId,
    p_start_date: startDate || null,
    p_end_date: endDate || null,
    p_page: page,
    p_limit: limit,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getRecoveryConfig = async (clientId) => {
  // Value-verified 2026-06-19: postmark_sender_verified, has_enabled_sequences,
  // can_enable_auto_send and all other fields match Express. Note backend
  // currently hardcodes postmark_sender_verified=true until the Postmark
  // webhook lands — no frontend change needed when that goes live.
  const { data, error } = await supabase.rpc('fn_get_recovery_config', {
    p_client_id: clientId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const updateRecoveryConfig = async (clientId, updates) => {
  // Migrated 2026-07-01. Was 42703 (join on nonexistent `clients.org_id`).
  // New RPC joins `clients.organization_id = members.org_id` gated on
  // removed_at IS NULL, and accepts flat column params (not a blob) so PostgREST
  // can bind them positionally. We only forward the fields the RPC accepts;
  // everything else silently drops.
  const rpcParams = { p_client_id: clientId };
  const aliases = {
    auto_recovery_enabled:          'p_auto_recovery_enabled',
    auto_recovery_sender_name:      'p_auto_recovery_sender_name',
    auto_recovery_sender_email:     'p_auto_recovery_sender_email',
    recovery_custom_instructions:   'p_recovery_custom_instructions',
  };
  for (const [src, dst] of Object.entries(aliases)) {
    if (updates && updates[src] !== undefined) rpcParams[dst] = updates[src];
  }
  const { data, error } = await supabase.rpc('fn_update_recovery_config', rpcParams);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// =====================================================
// FEATURE TOGGLE APIs
// =====================================================

// 2026-07-08: `feature_flags` reads go through fn_admin_get_feature_flags
// (SECURITY DEFINER, platform_admin only). Direct table read is blocked by
// RLS. Consumers read `.data.project_features` — legacy shape preserved,
// each row still exposes both `flag_key` (native) and `key` (alias).
export const getFeatureRegistry = async () => {
  const { data, error } = await supabase.rpc('fn_admin_get_feature_flags');
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  const rows = Array.isArray(data) ? data : (data?.flags || []);
  const shaped = rows
    .map((r) => ({
      ...r,
      key: r.flag_key,
      name: r.description || r.flag_key,
    }))
    .sort((a, b) => (a.flag_key || '').localeCompare(b.flag_key || ''));
  return { error: false, data: { project_features: shaped, features: shaped }, message: 'ok' };
};

// 2026-07-08: platform_admin toggles a feature flag scoped to global/org/user/client.
// Backend accepts { p_flag_key, p_scope, p_scope_id, p_enabled, p_config }.
// `feature_flags.config` is NOT NULL — default to `{}` so callers that don't
// care don't hit a 23502 constraint violation.
export const setFeatureFlag = async ({ flagKey, scope = 'global', scopeId = null, enabled, config = {} }) => {
  const { data, error } = await supabase.rpc('fn_admin_set_feature_flag', {
    p_flag_key: flagKey,
    p_scope: scope,
    p_scope_id: scopeId,
    p_enabled: enabled,
    p_config: config ?? {},
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Feature flag saved' };
};

// GCP-1 Wave B — 2026-07-06. fn_get_my_features returns a flat {key:bool} map
// with namespaced keys (dashboard.*, feature.*) per project memory. The
// FeaturesContext consumer reads `.data.features` — shape to preserve.
export const getMyFeatures = async () => {
  const { data, error } = await supabase.rpc('fn_get_my_features', { p_client_id: null });
  if (error) throw { message: error.message };
  return { error: false, data: { features: data || {} }, message: 'ok' };
};

// 2026-07-08 (v2): AddEditUserModal expects `.data.features` to be an ARRAY
// of `{key, name, enabled}` rows (it maps + toggles per-row). Resolve the
// full registry from `fn_admin_get_feature_flags` then overlay per-user
// scope='user'/scope_id=userId rows on top of scope='global' defaults.
export const getUserFeatures = async (userId) => {
  const { data, error } = await supabase.rpc('fn_admin_get_feature_flags');
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  const rows = Array.isArray(data) ? data : (data?.flags || []);
  // Build the effective feature list keyed by flag_key. Global rows seed the
  // list; user-scoped rows for this userId override enabled.
  const byKey = new Map();
  for (const r of rows) {
    if (!r?.flag_key) continue;
    if (r.scope === 'global') {
      byKey.set(r.flag_key, {
        key: r.flag_key,
        flag_key: r.flag_key,
        name: r.description || r.flag_key,
        enabled: !!r.enabled,
      });
    }
  }
  for (const r of rows) {
    if (r?.scope === 'user' && r.scope_id === userId && r.flag_key) {
      const existing = byKey.get(r.flag_key) || {
        key: r.flag_key,
        flag_key: r.flag_key,
        name: r.description || r.flag_key,
        enabled: false,
      };
      byKey.set(r.flag_key, { ...existing, enabled: !!r.enabled });
    }
  }
  const features = Array.from(byKey.values()).sort((a, b) => a.key.localeCompare(b.key));
  return { error: false, data: { features }, message: 'ok' };
};

// GCP-1 Wave B — Supabase has no generic user_preferences table. The scoped
// profile fields (theme, timezone, locale, navigation) come from
// fn_get_my_profile / fn_update_my_profile. Dashboard prefs like
// session_columns and allowed_clients don't have a Supabase home yet, so
// we route them through localStorage until backend adds a preferences JSONB
// column to `profiles` (or a `dashboard_preferences` table). BookingsPage
// already mirrors session_columns to localStorage — no UX regression.
const DASHBOARD_PREFS_KEY = 'dashboard_preferences';
const readLocalPrefs = () => {
  try { return JSON.parse(localStorage.getItem(DASHBOARD_PREFS_KEY) || '{}'); } catch { return {}; }
};

export const getMyPreferences = async () => {
  const local = readLocalPrefs();
  const { data: profile, error } = await supabase.rpc('fn_get_my_profile');
  if (error) throw { message: error.message };
  const p = Array.isArray(profile) ? profile[0] : profile || {};
  return {
    error: false,
    data: {
      preferences: {
        // Scoped profile fields (server-backed via fn_update_my_profile)
        theme_preference: p.theme_preference,
        navigation_preference: p.navigation_preference,
        timezone: p.timezone,
        locale: p.locale,
        // Dashboard prefs (localStorage-backed until backend adds JSONB column)
        session_columns: local.session_columns,
        allowed_clients: local.allowed_clients,
        ...local,
      },
    },
    message: 'ok',
  };
};

export const updateMyPreferences = async (preferences = {}) => {
  const scopedFields = ['theme_preference', 'navigation_preference', 'timezone', 'locale', 'display_name', 'first_name', 'last_name', 'bio'];
  const scoped = {}, dashboard = {};
  for (const [k, v] of Object.entries(preferences)) {
    if (scopedFields.includes(k)) scoped[k] = v; else dashboard[k] = v;
  }
  // Server: profile fields via fn_update_my_profile
  if (Object.keys(scoped).length) {
    const params = {
      p_bio: scoped.bio ?? null,
      p_display_name: scoped.display_name ?? null,
      p_first_name: scoped.first_name ?? null,
      p_last_name: scoped.last_name ?? null,
      p_locale: scoped.locale ?? null,
      p_navigation_preference: scoped.navigation_preference ?? null,
      p_theme_preference: scoped.theme_preference ?? null,
      p_timezone: scoped.timezone ?? null,
    };
    const { error } = await supabase.rpc('fn_update_my_profile', params);
    if (error) throw { message: error.message };
  }
  // Client: dashboard prefs to localStorage
  if (Object.keys(dashboard).length) {
    const merged = { ...readLocalPrefs(), ...dashboard };
    localStorage.setItem(DASHBOARD_PREFS_KEY, JSON.stringify(merged));
  }
  return { error: false, data: { preferences }, message: 'Preferences saved' };
};

// GCP-1 Wave D — 2026-07-07 (v2): direct `profiles` select cross-user hits the
// same `organization_members` RLS recursion (42P17). Reads for OTHER users go
// through fn_list_org_members (SECURITY DEFINER) which side-steps RLS and
// returns the same profile fields. Consumer (`AddEditUserModal`) reads
// `.data.preferences.allowed_clients` — shape preserved.
export const getUserPreferences = async (userId) => {
  const { data: sess } = await supabase.auth.getSession();
  const isSelf = userId && sess?.session?.user?.id && String(userId) === String(sess.session.user.id);
  if (isSelf) return getMyPreferences();
  // Cross-user path: iterate the caller's orgs and find the target user's row.
  const { data: orgs } = await supabase.rpc('fn_get_my_organizations');
  const orgIds = (orgs || []).map((o) => o.id || o.org_id).filter(Boolean);
  let member = null;
  for (const orgId of orgIds) {
    const { data } = await supabase.rpc('fn_list_org_members', { p_org_id: orgId });
    member = (data || []).find((m) => m.user_id === userId);
    if (member) break;
  }
  // fn_list_org_members doesn't return theme/tz/locale — those live on
  // `profiles`. Since direct table read is blocked and there's no
  // fn_get_user_profile RPC yet, return nulls with the correct shape. Consumer
  // UI shows placeholders. TODO(backend): add fn_get_user_profile(p_user_id)
  // OR extend fn_list_org_members to include profile prefs.
  return {
    error: false,
    data: {
      preferences: {
        theme_preference: null,
        navigation_preference: null,
        timezone: null,
        locale: null,
        allowed_clients: [],
        // Include what we DO have from the members RPC
        display_name: member?.display_name || null,
        email: member?.email || null,
      },
    },
    message: 'ok',
  };
};

// GCP-1 Deliverable #1 — shipped 2026-07-08. Cross-user profile/preferences
// updates via fn_admin_update_user_preferences (platform_admin only).
export const updateUserPreferences = async (userId, preferences) => {
  const { data, error } = await supabase.rpc('fn_admin_update_user_preferences', {
    p_user_id: userId,
    p_updates: preferences,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: 'Preferences saved' };
};

// 2026-07-10: backend's fn_admin_update_user_features AND fn_admin_set_feature_flag
// both insert into `feature_flags` without setting `config`, hitting the 23502
// NOT NULL constraint. Workaround: direct upsert with `config: {}` explicit.
// If RLS blocks direct writes (42501), fall back to the RPCs so the frontend
// still tries the "official" path. Remove this workaround once backend defaults
// `config` to `'{}'::jsonb` in either RPC.
export const updateUserFeatures = async (userId, features) => {
  const entries = Object.entries(features || {});
  if (!entries.length) return { error: false, data: { updated: 0 }, message: 'No features to update' };

  const rows = entries.map(([flagKey, enabled]) => ({
    flag_key: flagKey,
    scope: 'user',
    scope_id: userId,
    enabled: !!enabled,
    config: {},
  }));

  const directRes = await supabase
    .from('feature_flags')
    .upsert(rows, { onConflict: 'flag_key,scope,scope_id' })
    .select('id');
  if (!directRes.error) {
    return { error: false, data: { updated: entries.length }, message: 'Features updated' };
  }

  // Direct write blocked (typically RLS 42501). Fall through to RPCs, which
  // are known to be broken on config-null but try them for completeness so
  // the error surface tells us WHICH gate failed.
  const bulkRes = await supabase.rpc('fn_admin_update_user_features', {
    p_user_id: userId,
    p_features: features,
  });
  if (!bulkRes.error && !bulkRes.data?.error) {
    return { error: false, data: bulkRes.data, message: 'Features updated' };
  }

  const results = [];
  for (const [flagKey, enabled] of entries) {
    const { data, error } = await supabase.rpc('fn_admin_set_feature_flag', {
      p_flag_key: flagKey,
      p_scope: 'user',
      p_scope_id: userId,
      p_enabled: !!enabled,
      p_config: {},
    });
    if (error) results.push({ flagKey, error: error.message });
    else if (data?.error) results.push({ flagKey, error: data.message });
  }
  if (results.length) {
    throw {
      message: `Feature save blocked (direct: ${directRes.error.message}; ${results.length}/${entries.length} RPC failures)`,
      results,
    };
  }
  return { error: false, data: { updated: entries.length }, message: 'Features updated' };
};

// =====================================================
// GOOGLE ADS APIs
// =====================================================

export const getGoogleAdsFilters = async (clientId, { status, campaign_type, campaign_name } = {}) => {
  // Migrated 2026-07-02 per Google Ads Part 2 Re-Audit (2026-06-13). Union of
  // gads_campaign_settings + gads_campaigns_daily → 15 campaigns (was 5 pulled
  // from daily-only, missing the ones that never spent).
  void status; void campaign_type; void campaign_name;  // RPC returns the full roster
  const { data, error } = await supabase.rpc('fn_get_gads_filter_options', { p_client_id: clientId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// Review #3 (2026-07-23) — the Search Term Explorer's status filter can't be
// applied server-side: fn_get_gads_search_terms accepts no status param and its
// rows carry no campaign_status. fn_get_gads_filter_options is also status-blind.
// The only clean source of per-campaign status is fn_get_gads_campaign_settings
// (returns every campaign with a `status`), so expose a name→status map the
// page can use to filter search-term rows client-side by their campaign_name.
export const getGoogleAdsCampaignStatuses = async (clientId) => {
  const { data, error } = await supabase.rpc('fn_get_gads_campaign_settings', {
    p_client_id: clientId,
    p_campaign_name: null,
  });
  if (error) wrapSupabaseError(error);
  const rows = Array.isArray(data) ? data : (data?.items || []);
  const map = {};
  for (const r of rows) {
    if (r?.campaign_name) map[r.campaign_name] = r.status || null;
  }
  return wrapSupabase(map);
};

export const getGoogleAdsBudgetPacing = async (clientId, referenceDate = null) => {
  // Migrated to Supabase RPC 2026-06-13 (verified value-clean vs Cloud Run; RPC
  // adds 6 additive fields: days_elapsed, days_in_month, days_with_data,
  // last_data_date, expected_mtd_spend, total_daily_budget).
  const params = { p_client_id: clientId };
  if (referenceDate) params.p_reference_date = referenceDate;
  const { data, error } = await supabase.rpc('gads_budget_pacing', params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsSpendTrend = async (clientId, startDate, endDate, campaignStatus = null, campaigns = null) => {
  // Updated 2026-07-02 — p_campaign_status now supported (only ENABLED exists
  // in Verde data today, so it's a correct no-op until paused data lands).
  const params = { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate, p_campaigns: campaigns };
  if (campaignStatus) params.p_campaign_status = campaignStatus;
  const { data, error } = await supabase.rpc('fn_get_gads_spend_trend', params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsWastedSpend = async (clientId, startDate, endDate, params = {}) => {
  // Migrated 2026-06-13 — all filters supported (campaigns + pagination).
  const { data, error } = await supabase.rpc('fn_get_gads_wasted_spend', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_campaigns: params.campaigns ?? null,
    p_page: params.page ?? 1,
    p_limit: params.limit ?? 25,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsSearchTerms = async (clientId, startDate, endDate, params = {}) => {
  // Migrated 2026-07-02. Unified on term×keyword×adgroup×match row grain.
  // total_items=2,532 (rows); summary.total_unique_terms=1,276 (distinct terms).
  // non_converting_terms_with_spend=410 (accurate to name — a term that
  // converts overall is NOT non-converting); non_converting_rows=733 (granular).
  const { data, error } = await supabase.rpc('fn_get_gads_search_terms', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_campaigns: params.campaigns ?? null,
    p_campaign_name: params.campaign_name ?? null,
    p_page: params.page ?? 1,
    p_limit: params.limit ?? 25,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsCampaigns = async (clientId, startDate, endDate, params = {}) => {
  // Migrated 2026-06-13 — campaign_name + pagination supported.
  // (Default page-size differs: Express=25, RPC=100. Pass an explicit limit to
  // keep table page sizes stable.)
  //
  // Review #3 (2026-07-23): the page passes `campaign_status` (e.g. 'ENABLED'),
  // but the RPC's argument is `p_status` — NOT `p_campaign_status` (verified
  // against the live PostgREST signature). Forwarding it here makes the
  // Enabled-only filter exclude PAUSED campaigns server-side, which keeps the
  // pagination totals consistent with the visible rows.
  const { data, error } = await supabase.rpc('fn_get_gads_campaigns', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_campaign_name: params.campaign_name ?? null,
    p_status: params.campaign_status ?? null,
    p_page: params.page ?? 1,
    p_limit: params.limit ?? 25,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsCampaignDrill = async (clientId, campaignName, tab, startDate, endDate, params = {}) => {
  // Migrated 2026-07-02. Dispatches to the right RPC per tab. `overview` uses
  // the new fn_get_gads_campaign_overview returning {trend, summary}; keywords/
  // ad_groups/ads wrap into {items, pagination} with p_page/p_limit; settings
  // takes no date range.
  const p_client_id = clientId;
  const p_campaign_name = campaignName;
  const p_start_date = startDate;
  const p_end_date = endDate;
  const p_page = params.page ?? 1;
  const p_limit = params.limit ?? 100;
  let rpc, rpcParams;
  switch (tab) {
    case 'overview':
      rpc = 'fn_get_gads_campaign_overview';
      rpcParams = { p_client_id, p_start_date, p_end_date, p_campaign_name };
      break;
    case 'searchTerms':
    case 'search-terms':
      rpc = 'fn_get_gads_search_terms';
      rpcParams = { p_client_id, p_start_date, p_end_date, p_campaigns: null, p_campaign_name, p_page, p_limit };
      break;
    case 'keywords':
      rpc = 'fn_get_gads_keywords';
      rpcParams = { p_client_id, p_start_date, p_end_date, p_campaign_name, p_page, p_limit };
      break;
    case 'adGroups':
    case 'ad-groups':
      rpc = 'fn_get_gads_ad_groups';
      rpcParams = { p_client_id, p_start_date, p_end_date, p_campaign_name, p_page, p_limit };
      break;
    case 'ads':
      rpc = 'fn_get_gads_ads';
      rpcParams = { p_client_id, p_start_date, p_end_date, p_campaign_name, p_page, p_limit };
      break;
    case 'settings':
      rpc = 'fn_get_gads_campaign_settings';
      rpcParams = { p_client_id, p_campaign_name };
      break;
    default:
      wrapSupabaseError({ message: `Unknown drill tab: ${tab}` });
  }
  const { data, error } = await supabase.rpc(rpc, rpcParams);
  if (error) wrapSupabaseError(error);
  // Review #5 (2026-07-23): fn_get_gads_campaign_settings returns an ARRAY of
  // rows (one per campaign) even when p_campaign_name is passed. The Settings
  // panel expects a single settings object, so reading cfg.status off the array
  // was always undefined → every field blank and Status defaulted to "Paused".
  // Unwrap to the single matching row (fall back to the first/only row).
  if (tab === 'settings') {
    const rows = Array.isArray(data) ? data : (data?.items || (data ? [data] : []));
    const match = rows.find((r) => r?.campaign_name === campaignName) || rows[0] || null;
    return wrapSupabase(match);
  }
  return wrapSupabase(data);
};

export const getGoogleAdsKpis = async (clientId, startDate, endDate, comparison = 'none', { campaign_status, campaigns } = {}) => {
  // Migrated 2026-06-13. RPC headline values match Cloud Run byte-for-byte
  // (spend, ctr, roas, tier1_bookings, tier1_value, tier2_phone_calls,
  // all_conversions, all_conversion_value). 3 sub-key drifts in `comparison`
  // are key renames only.
  //
  // Updated 2026-07-02 per Part 2 Re-Audit — RPC now accepts p_comparison,
  // p_campaigns, p_campaign_status. Campaign scoping filters the SPEND side
  // only; conversion tiers stay account-level (gads_conversion_actions_daily
  // has no campaign column). RPC exposes `tier_breakdown_is_account_level`
  // flag when scoped, and nulls cost_per_booking / roas so filtered spend
  // isn't divided by account bookings.
  const params = {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_comparison: comparison ?? 'none',
  };
  if (campaigns) params.p_campaigns = Array.isArray(campaigns) ? campaigns : [campaigns];
  if (campaign_status) params.p_campaign_status = campaign_status;
  const { data, error } = await supabase.rpc('fn_get_gads_kpis', params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// =====================================================
// GOOGLE ADS DIMENSION APIs
// =====================================================

export const getGoogleAdsDimDevice = async (clientId, startDate, endDate, campaigns = null) => {
  // Updated 2026-07-02 — p_campaigns now supported (device_daily has real
  // campaign column).
  const params = { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate };
  if (campaigns) params.p_campaigns = Array.isArray(campaigns) ? campaigns : [campaigns];
  const { data, error } = await supabase.rpc('fn_get_gads_device_performance', params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsDimHourly = async (clientId, startDate, endDate, campaigns = null) => {
  // Migrated 2026-07-02. Fixed the containment-filter double-count on
  // overlapping rolling snapshots — RPC now prefers clean daily snapshots in
  // range, else the latest single window. Ingestion is stale through 2026-04-06
  // (data caveat §5); auto-corrects when daily ingestion resumes.
  void campaigns;  // hourly/dow don't accept campaigns filter
  const { data, error } = await supabase.rpc('fn_get_gads_hourly_performance', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsDimDayOfWeek = async (clientId, startDate, endDate, campaigns = null) => {
  // Migrated 2026-07-02. Same de-overlap fix as hourly + canonical Monday→Sunday
  // ordering (was Friday-start with no ORDER BY).
  void campaigns;
  const { data, error } = await supabase.rpc('fn_get_gads_dow_performance', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsDimGeo = async (clientId, startDate, endDate, page = 1, limit = 25, campaigns = null) => {
  // Migrated 2026-06-13. Pagination supported; RPC adds one extra field
  // (conversion_share) — additive, frontend ignores by default.
  // ⚠️ Migration regression: campaigns filter not accepted. TODO(backend): add p_campaigns.
  void campaigns;
  const { data, error } = await supabase.rpc('fn_get_gads_geo_performance', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_page: page,
    p_limit: limit,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsConversionsBreakdown = async (clientId, startDate, endDate) => {
  // Migrated 2026-07-02. tier2 now correctly splits PHONE_CALL_LEAD out of
  // leads: tier2_leads = 72, tier2_phone_calls = 140 — reconciles to
  // fn_get_gads_kpis (was lumping into 212, mismatching KPI).
  //
  // 2026-07-13 shape adapter — Cloud Run returned
  //   { summary, tiers: [{tier: "Tier 1: Bookings", total_conversions, total_value, items: [...]}, ...] }
  // Supabase returns { summary, tiers: [{tier: 1, action_name, category, conversions, value}, ...] }
  // (flat: 1 row per conversion action, no per-tier grouping/labels/totals).
  // Roll up flat rows into the nested shape the ConversionBreakdown component
  // expects.
  const { data, error } = await supabase.rpc('fn_get_gads_conversion_breakdown', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);

  const TIER_LABELS = {
    1: 'Tier 1: Bookings',
    2: 'Tier 2: Leads',
    3: 'Tier 3: Soft Engagement',
  };
  const flat = Array.isArray(data?.tiers) ? data.tiers : [];
  const groups = new Map();
  for (const row of flat) {
    const t = Number(row.tier) || 0;
    if (!groups.has(t)) groups.set(t, { tier: t, total_conversions: 0, total_value: 0, items: [] });
    const g = groups.get(t);
    const conversions = Number(row.conversions) || 0;
    const value = Number(row.value) || 0;
    g.total_conversions += conversions;
    g.total_value += value;
    g.items.push({
      conversion_action: row.action_name || '',
      conversion_type: row.category || '',
      conversions,
      conversion_value: value,
    });
  }
  const tiers = [1, 2, 3]
    .filter((t) => groups.has(t))
    .map((t) => {
      const g = groups.get(t);
      // sort items by conversions descending — matches Cloud Run
      g.items.sort((a, b) => (b.conversions || 0) - (a.conversions || 0));
      return { ...g, tier: TIER_LABELS[t] || `Tier ${t}` };
    });
  return wrapSupabase({ summary: data?.summary || {}, tiers });
};

export const getGoogleAdsAdCopyInsights = async (clientId, startDate, endDate, campaigns = null) => {
  // Migrated 2026-07-02. Per-ad view kept (shows actual ad_title copy) with
  // avg_cpc added — Cloud Run was per-ad-type (avg_cpc rollup, no copy).
  // Product decision §5: this panel is "ad copy insights", so the copy matters.
  void campaigns;
  const { data, error } = await supabase.rpc('fn_get_gads_ad_copy_insights', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsRecommendations = async (clientId) => {
  // Migrated 2026-07-02. last_seen now uses extracted_at (when Google reported)
  // not created_at (DB write). Deterministic order: count desc, alert_type asc.
  const { data, error } = await supabase.rpc('fn_get_gads_recommendations', { p_client_id: clientId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsClientInfo = async (clientId) => {
  // Migrated 2026-06-13. RPC returns the 4 fields (id, campground_name, city,
  // state) the page consumes — verified byte-identical vs Cloud Run.
  const { data, error } = await supabase.rpc('fn_get_gads_client_info', { p_client_id: clientId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGoogleAdsLastUpdated = async (clientId) => {
  // 2026-07-23 — the direct `.from('gads_campaigns_daily')` read is blocked by
  // RLS (42501 permission denied) for authenticated users, so it always threw
  // and left `lastUpdated` null on the frontend. With lastUpdated null,
  // isPartialPeriod can never be true, which silently disabled the entire
  // "(period so far)" clamp + badge + like-for-like A4 comparison.
  //
  // The `gads_budget_pacing` RPC (SECURITY DEFINER, already called by this page)
  // exposes `last_data_date` — the same max(date) value — without hitting RLS.
  // Use it as the source of truth. Verified: returns "2026-07-21" for Verde,
  // matching Staging's "Data through" and the spend-trend's last row.
  const { data, error } = await supabase.rpc('gads_budget_pacing', { p_client_id: clientId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase({ latest_data_date: data?.last_data_date ?? null });
};

/* ==================== Google Analytics (GA4) ==================== */

// ── Google Analytics (GA4) — Supabase-direct migrations ──
// Migrated 2026-06-08 after side-by-side verification with populated data
// (Verde Ranch, 2026-01-01 → 2026-06-08):
//   - getGA4Kpis           — values match Cloud Run exactly (sessions=84,071, conversions=1,645)
//   - getGA4TrafficTrend   — values match Cloud Run exactly (per-day)
// Channels + LandingPages were NOT migrated despite matching shape — values
// differ materially between Cloud Run and Supabase (Channels ~24% off,
// LandingPages 10× off). See docs/google-analytics-module-supabase-migration-issues.md
// for the full per-endpoint analysis with captured numbers.

export const getGA4Kpis = async (clientId, startDate, endDate, comparison = 'none') => {
  // 2026-06-13: backend now accepts p_comparison ('none'|'previous_period'|
  // 'previous_year'). 'none' returns comparison:null + previous_period:null
  // natively, so the temporary strip-the-block adapter is removed.
  const { data, error } = await supabase.rpc('fn_get_ga4_kpis', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_comparison: comparison,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGA4TrafficTrend = async (clientId, startDate, endDate) => {
  const { data, error } = await supabase.rpc('fn_get_ga4_traffic_trend', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGA4Traffic = async (clientId, startDate, endDate) => {
  // Migrated 2026-06-13. RPC values match Cloud Run sum-of-sessions (87,519);
  // row order is asc-by-date in Supabase, desc-by-date in Express. Consumers
  // sort client-side already (TrafficTrend chart maps to recharts which
  // re-orders), so the order difference is invisible.
  const { data, error } = await supabase.rpc('fn_get_ga4_traffic', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGA4Channels = async (clientId, startDate, endDate) => {
  // Migrated 2026-06-13 per backend doc section 1.1.
  // ⚠️ Intentional value change: Supabase returns ~22% MORE sessions than
  // Cloud Run did (84,021 vs 65,298) AND a new "AI Assistant" channel.
  // This is a correction — Cloud Run was dropping data. Expect the channels
  // table totals to jump after migration; this is the new source of truth.
  const { data, error } = await supabase.rpc('fn_get_ga4_channels', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// ─── GA4 migration 2026-07-02 per Part 2 Re-Audit backend doc (2026-06-13) ──
// 23 helpers migrated in one pass. Windowed helpers now return {items, window}
// so the frontend can render a "7-day snapshot ending <date>" label directly
// from the data response.
//
// ─── 2026-07-30: the 7 windows-picker helpers are NO LONGER stubs ────────────
// They were shipped as `[]` on the migration-doc §3 assumption that the window
// concept had been folded into each data RPC's `{items, window}` envelope and no
// pickable list survived. That was wrong: all seven `fn_get_ga4_*_windows` RPCs
// exist and return 614-616 windows. Because the wrappers returned `[]`, the
// picker dropdowns and the Geo Trend multi-window chart could never populate
// (migration-review "backend-4" — a frontend gap, not a missing function).
//
// ⚠️ These RPCs take `p_client_id` ONLY. Passing p_start_date/p_end_date returns
// 404 PGRST202 (verified live 2026-07-30 on all seven). So the overlap filtering
// that `useGA4Windows` documents as server-side must happen HERE, client-side —
// unfiltered the list spans 2021-04-01 through today and would put ~616 entries
// in a dropdown scoped to a 30-day range.

async function ga4Rpc(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

// Keep windows whose 7-day span overlaps [startDate, endDate] at all — a window
// straddling either edge is still partially in range, so compare start-vs-end and
// end-vs-start rather than requiring full containment. Bounds are optional and
// independent; ISO yyyy-mm-dd compares correctly as a string, so no Date parsing
// (which would reintroduce the UTC-shift class of bug).
const overlapsRange = (win, startDate, endDate) => {
  if (startDate && win.date_end < startDate) return false;
  if (endDate && win.date_start > endDate) return false;
  return true;
};

// Newest-first, so `useGA4Windows` falling back to `w[0]` selects the most recent
// window. The RPCs already return that order; sorting makes the contract explicit
// rather than inherited from the RPC's ORDER BY.
const ga4WindowsRpc = async (name, clientId, { startDate, endDate } = {}, label) => {
  const { data, error } = await supabase.rpc(name, { p_client_id: clientId });
  if (error) wrapSupabaseError(error, label);
  const windows = (Array.isArray(data) ? data : [])
    .filter((win) => win?.date_start && win?.date_end && overlapsRange(win, startDate, endDate))
    .sort((a, b) => (a.date_start < b.date_start ? 1 : a.date_start > b.date_start ? -1 : 0));
  return wrapSupabase(windows);
};

export const getGA4ChannelTrend = (clientId, startDate, endDate) =>
  // Doc §4: channels table is clean; Supabase returns the full 1,317 rows
  // vs Cloud Run's truncated 1,000. Ship as a correction.
  ga4Rpc('fn_get_ga4_channel_trend', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'channel trend');

export const getGA4SourceMedium = (clientId, startDate, endDate, params = {}) =>
  // Doc §1: deduped daily rows. 87,480 sessions reconcile to KPI 87,519.
  ga4Rpc('fn_get_ga4_source_medium', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_page: params.page ?? 1, p_limit: params.limit ?? 25,
  }, 'source/medium');

export const getGA4SourceMediumTrend = (clientId, startDate, endDate) =>
  // Doc §4: new build (was undefined on Express). Top-10 combos + "other".
  ga4Rpc('fn_get_ga4_source_medium_trend', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'source/medium trend');

export const getGA4Referrals = (clientId, startDate, endDate, params = {}) =>
  // Doc §3: windowed — returns {items, window} envelope.
  ga4Rpc('fn_get_ga4_referrals', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_page: params.page ?? 1, p_limit: params.limit ?? 25,
  }, 'referrals');

export const getGA4ReferralWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_referral_windows', clientId, opts, 'referral windows');

export const getGA4Campaigns = (clientId, startDate, endDate, params = {}) =>
  // Doc §1: deduped. 87,489 reconciles to KPI 87,519.
  ga4Rpc('fn_get_ga4_campaigns', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_page: params.page ?? 1, p_limit: params.limit ?? 25,
  }, 'campaigns');

export const getGA4PageWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_page_windows', clientId, opts, 'page windows');

export const getGA4Pages = (clientId, startDate, endDate, params = {}) =>
  // Doc §3: windowed {items, window}.
  ga4Rpc('fn_get_ga4_pages_paginated', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_page: params.page ?? 1, p_limit: params.limit ?? 25,
  }, 'pages');

export const getGA4LandingPages = async (clientId, startDate, endDate) => {
  // Migrated 2026-06-13 per backend doc section 1.2.
  // ⚠️ This is a RANKING tool, NOT a session total. GA4 records one
  // landing-page row per day, so a guest who visits in January and again
  // in March is counted in both daily rows. Summing across long ranges
  // inflates beyond unique sessions (~107% of KPI total for a 5-month
  // range). Render as a ranked list — the relative order is correct
  // and useful. Don't sum the sessions column to compare against KPIs.
  const { data, error } = await supabase.rpc('fn_get_ga4_landing_pages', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGA4GeoWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_geo_windows', clientId, opts, 'geo windows');

export const getGA4GeoRegion = (clientId, startDate, endDate, country = null) =>
  // Windowed {items, window}. Country param filters regions within a country.
  ga4Rpc('fn_get_ga4_geo_region', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_country: country,
  }, 'geo region');

export const getGA4GeoCity = (clientId, startDate, endDate, country, region) =>
  // Cities within (country, region). Uses the same fn_get_ga4_geo_region RPC
  // per backend doc — passing both country + region drills to city level.
  ga4Rpc('fn_get_ga4_geo_region', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_country: country, p_region: region,
  }, 'geo city');

export const getGA4Devices = async (clientId, startDate, endDate) => {
  // Migrated 2026-06-13. Verified byte-identical values vs Cloud Run
  // (only JSON object key order differs; JS doesn't preserve key order
  // as a semantic property).
  const { data, error } = await supabase.rpc('fn_get_ga4_devices', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGA4BrowserWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_browser_windows', clientId, opts, 'browser windows');

export const getGA4Browsers = (clientId, startDate, endDate) =>
  // Doc §2 shape fix: separate browsers table (was reusing fn_get_ga4_devices).
  // Windowed {items, window}.
  ga4Rpc('fn_get_ga4_browsers', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'browsers');

export const getGA4OSWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_os_windows', clientId, opts, 'OS windows');

export const getGA4OperatingSystems = (clientId, startDate, endDate) =>
  // Windowed {items, window}.
  ga4Rpc('fn_get_ga4_operating_systems', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'operating systems');

export const getGA4Events = (clientId, startDate, endDate, params = {}) =>
  // Doc §4: already correct — page_view = 287,018 matches pageviews.
  // Cloud Run's 76,255 was under-counting. Ship as-is.
  ga4Rpc('fn_get_ga4_events', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_page: params.page ?? 1, p_limit: params.limit ?? 25,
  }, 'events');

export const getGA4Conversions = async (clientId, startDate, endDate) => {
  // Migrated 2026-06-13. Verified MATCH vs Cloud Run — both return a single
  // aggregated row for the 'purchase' event by default.
  const { data, error } = await supabase.rpc('fn_get_ga4_conversions', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getGA4ConversionsBySource = (clientId, startDate, endDate, eventName) =>
  // Doc §2 new build: source+medium breakdown of purchases (deduped). Total
  // reconciles to purchase count (1,690 for Verde year-wide).
  ga4Rpc('fn_get_ga4_conversions_by_source', {
    p_client_id: clientId, p_start_date: startDate, p_end_date: endDate,
    p_event_name: eventName ?? 'purchase',
  }, 'conversions by source');

export const getGA4ChartUserType = (clientId, startDate, endDate) =>
  // Doc §2 shape fix: was per-day rows, now rollup with new/returning/unknown
  // summing to KPI (60,459 / 21,386 / 5,679 for Verde).
  ga4Rpc('fn_get_ga4_user_type', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'user type');

// Note the RPC names here are `fn_get_ga4_hourly_windows` / `fn_get_ga4_dow_windows`
// — they do NOT carry the `chart_`/`_traffic_` infixes the JS helper names do, and
// the mismatched guesses 404. Names confirmed against the PostgREST schema.
export const getGA4ChartHourlyTrafficWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_hourly_windows', clientId, opts, 'hourly traffic windows');

export const getGA4ChartHourlyTraffic = (clientId, startDate, endDate) =>
  // Doc §3: windowed {items, window}. 24 hourly items in the returned week.
  ga4Rpc('fn_get_ga4_hourly_traffic', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'hourly traffic');

export const getGA4ChartDayOfWeekWindows = (clientId, opts) =>
  ga4WindowsRpc('fn_get_ga4_dow_windows', clientId, opts, 'day of week windows');

export const getGA4ChartDayOfWeek = (clientId, startDate, endDate) =>
  // Doc §3: windowed {items, window}. 7 day-of-week items.
  ga4Rpc('fn_get_ga4_dow_traffic', { p_client_id: clientId, p_start_date: startDate, p_end_date: endDate }, 'day of week');

export const getGA4LastUpdated = (clientId) =>
  // Doc §2 shape fix: returns full extracted_at timestamp (was date-only).
  ga4Rpc('fn_get_ga4_last_updated', { p_client_id: clientId }, 'last updated');

// ─── CDP (Visitor Intelligence) ────────────────────────────────────────────
// P1-4 (punch list 2026-06-05): all visitor-counting endpoints exclude bots
// (traffic_channel_group='Bot') by default server-side. `includeBots` option
// preserved on the helper signatures for API-shape stability, but no longer
// forwarded — RPCs ignore it.
//
// ─── CDP migrated 2026-07-08 (Part 4 + Part 2 handoff) ─────────────────
// All 23 CDP helpers now route through Supabase RPCs. Attribution paths +
// content influence are backed by real `fn_get_cdp_*` implementations
// (Deliverables #3 + #4 shipped 2026-07-08) — no more Cloud Run passthrough.
//
// Note: CDP RPCs (fn_get_cdp_*) are all read-only SELECTs, so cdpRpc now routes
// through rpcReadWithRetry — a cold plan can blow Postgres' statement_timeout
// (57014) on the first call, then succeed in under 2s on the warm retry.
// Previously cdpRpc was a single bare attempt; fn_get_cdp_ai_tech and
// fn_get_cdp_insights were both observed returning 57014 on first load.

async function cdpRpc(name, params, label) {
  const { data, error } = await rpcReadWithRetry(name, params, CDP_RETRY_DELAYS_MS);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

export const getCdpActiveVisitors = (clientId, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_active_visitors', { p_client_id: clientId }, 'active visitors');
};

export const getCdpRecentEvents = (clientId, limit = 30, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_recent_events', { p_client_id: clientId, p_limit: limit }, 'recent events');
};

export const getCdpEnrichVisitor = (clientId, cvId) =>
  // Returns null (not error) when cv_id has no match — frontend renders
  // "No data for this cv_id" empty state.
  cdpRpc('fn_get_cdp_enrich_visitor', { p_client_id: clientId, p_cv_id: cvId }, 'enrich visitor');

export const getCdpKpis = (clientId, startDate, endDate, { includeBots = false } = {}) =>
  cdpRpc('fn_get_cdp_kpis', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_include_bots: includeBots,
  }, 'CDP KPIs');

export const getCdpTrend = (clientId, startDate, endDate, { includeBots = false } = {}) =>
  cdpRpc('fn_get_cdp_trend', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_include_bots: includeBots,
  }, 'CDP trend');

export const getCdpSessions = (clientId, startDate, endDate, params = {}) => {
  // The deployed RPC accepts only (p_client_id, p_start_date, p_end_date,
  // p_page, p_limit, p_filter). Fine-grained filters (channel, device,
  // visitor_type, location, landing_page) are not yet server-side filterable;
  // consumers can slice client-side or pass a single `filter` token that
  // matches a controller-defined enum (converted, bounced, etc.).
  void params.channel; void params.device; void params.visitor_type;
  void params.location; void params.landing_page; void params.includeBots;
  return cdpRpc('fn_get_cdp_sessions', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_page: params.page || 1,
    p_limit: params.limit || 20,
    p_filter: params.filter || null,
  }, 'CDP sessions');
};

export const getCdpSessionDetail = (clientId, sessionId) =>
  cdpRpc('fn_get_cdp_session_detail', { p_client_id: clientId, p_session_id: sessionId }, 'session detail');

export const getCdpSegments = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  // RPC has no p_include_bots — bot exclusion is baked in per Rule 4.
  void includeBots;
  return cdpRpc('fn_get_cdp_segments', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'CDP segments');
};

export const getCdpChannelPerformance = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_channel_performance', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'channel performance');
};

export const getCdpChannelCqs = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_channel_cqs', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'channel quality scores');
};

export const getCdpAttribution = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_attribution', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'attribution');
};

export const getCdpAttributionPaths = (clientId, startDate, endDate, { includeBots = false, limit = 20 } = {}) => {
  // 2026-07-08: Deliverable #3 shipped. Real multi-touch channel-sequence
  // attribution (replaces the Cloud Run passthrough that always returned []).
  // Response: [{ path: [channel, ...], conversions, avg_touchpoints }, ...].
  void includeBots;
  return cdpRpc('fn_get_cdp_attribution_paths', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_limit: limit,
  }, 'attribution paths');
};

export const getCdpFacebookDeepDive = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_facebook_deep_dive', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'Facebook deep-dive');
};

export const getCdpPeople = (clientId, startDate, endDate, params = {}) => {
  // Fine-grained filters (search, lifecycle, has_booking, multi_device) not
  // yet server-side filterable — the RPC accepts only page + limit. Consumers
  // can slice/filter client-side over the returned page.
  void params.search; void params.lifecycle;
  void params.has_booking; void params.multi_device;
  return cdpRpc('fn_get_cdp_people', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_page: params.page || 1,
    p_limit: params.limit || 20,
  }, 'people');
};

export const getCdpKnownRatio = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_known_ratio', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'known ratio');
};

export const getCdpPersonProfile = (clientId, { personId, cvId } = {}) => {
  // RPC identifies exclusively by person_id (bigint). If the caller passes a
  // cvId, resolve it via fn_get_cdp_enrich_visitor first — that RPC exposes
  // the person_id when the visitor is identified.
  if (personId != null) {
    return cdpRpc('fn_get_cdp_person_profile', { p_client_id: clientId, p_person_id: personId }, 'person profile');
  }
  if (cvId) {
    return (async () => {
      const enrich = await cdpRpc('fn_get_cdp_enrich_visitor', { p_client_id: clientId, p_cv_id: cvId }, 'enrich (for profile)');
      const resolvedId = enrich?.data?.person?.person_id;
      if (resolvedId == null) return wrapSupabase(null);
      return cdpRpc('fn_get_cdp_person_profile', { p_client_id: clientId, p_person_id: resolvedId }, 'person profile');
    })();
  }
  return Promise.resolve(wrapSupabase(null));
};

export const getCdpPersonJourney = (clientId, personId, { limit = 200 } = {}) =>
  cdpRpc('fn_get_cdp_person_journey', {
    p_client_id: clientId,
    p_person_id: personId,
    p_limit: limit,
  }, 'person journey');

export const getCdpAiTech = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  // AI Crawlers panel intentionally includes bot traffic — panel is *about*
  // crawlers. Human-visitor sub-panels within the same RPC still apply Rule 4.
  void includeBots;
  return cdpRpc('fn_get_cdp_ai_tech', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'AI & Tech data');
};

export const getCdpInsights = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  // ⚠️ Backend RPC was observed hitting statement_timeout (57014) at >60-day
  // ranges. cdpRpc now retries on 57014 (cold-plan recovery), so the common
  // 30-day default range is safe. Very wide ranges (>60d) may still exhaust
  // all retry attempts if the warm plan is also too slow — clamp client-side
  // if you need year-wide windows until the backend function is optimized.
  void includeBots;
  return cdpRpc('fn_get_cdp_insights', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'insights');
};

export const getCdpLandingPages = (clientId, startDate, endDate, { includeBots = false, limit = 50 } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_landing_pages', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_limit: limit,
  }, 'landing pages');
};

export const getCdpContentInfluence = (clientId, startDate, endDate, { includeBots = false, limit = 50 } = {}) => {
  // 2026-07-08: Deliverable #4 shipped. Landing-page conversion + influence
  // score (100 = baseline). Response: { baseline_conversion_rate, pages: [...] }.
  void includeBots;
  return cdpRpc('fn_get_cdp_content_influence', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_limit: limit,
  }, 'content influence');
};

export const getCdpPeakHours = (clientId, startDate, endDate, { includeBots = false } = {}) => {
  void includeBots;
  return cdpRpc('fn_get_cdp_peak_hours', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
  }, 'peak hours');
};

export const getCdpFunnel = (clientId, startDate, endDate, { includeBots = false } = {}) =>
  cdpRpc('fn_get_cdp_funnel', {
    p_client_id: clientId,
    p_start_date: startDate,
    p_end_date: endDate,
    p_include_bots: includeBots,
  }, 'CDP funnel');

// 2026-08-14 (Epic 10) — Guest Intelligence AI Sentence Layer.
// Reads the guest_intel_sentences table (not an RPC — a direct table read,
// same as resource_grants). Returns a flat { [panel_key]: sentence } map.
// Sentences are generated by a scheduled backend job; custom/arbitrary date
// ranges will have none. Treat as progressive enhancement — never block on it.
export const getGuestIntelSentences = async (clientId, startDate, endDate) => {
  const range = `${startDate}_${endDate}`;
  const { data, error } = await supabase
    .from('guest_intel_sentences')
    .select('panel_key, sentence, source, generated_at')
    .eq('client_id', clientId)
    .eq('date_range', range);
  if (error) {
    // RLS may block the read, or the table may not exist yet. Either way,
    // return an empty map — the dashboard renders normally without sentences.
    console.warn('[GuestIntel] sentences read failed:', error.message);
    return { error: false, data: {}, message: 'ok' };
  }
  const say = {};
  for (const r of (data || [])) {
    if (r.panel_key && r.sentence) say[r.panel_key] = r.sentence;
  }
  return { error: false, data: say, message: 'ok' };
};

// =====================================================
// EVENT INTELLIGENCE APIs
// =====================================================

const buildEiQs = (clientId, startDate, endDate) => {
  const qp = new URLSearchParams({ client_id: clientId });
  if (startDate) qp.set("start_date", startDate);
  if (endDate) qp.set("end_date", endDate);
  return qp.toString();
};

// ─── EI migration 2026-06-23 ───────────────────────────────────────────────
// 13 of 15 helpers verified value-clean against Cloud Run + migrated to direct
// supabase.rpc(). Surfaced set = ei_opportunities WHERE status <> 'clustered'
// (per backend doc §1) applied by every counting RPC. Most "drift" vs the
// migration doc snapshot is data growth (170 → 270 surfaced opportunities at
// verification time). getEiMarketingVelocity is sign-flipped intentionally
// (doc 4.1: avg_lead_days now positive — forward-looking instead of backfill).
//
// All 15 EI helpers now migrated. getEiPlaybookDetail joined the RPC set
// 2026-07-01 after we discovered the "missing" RPC was actually deployed —
// it just takes 2 args (p_playbook_id, p_client_id), and our audit probed
// only 1-arg signatures. Per EI Part 2 doc (backend 2026-06-23), the 2-arg
// signature is intentional for tenant isolation (WHERE o.client_id = p_client_id).

async function eiRpc(name, params, label) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

const eiDateParams = (s, e) => ({ p_start_date: s || null, p_end_date: e || null });

export const getEiEcp = (clientId) =>
  // Per doc 2.1: ECP = sum(opportunity_score) WHERE status <> 'clustered'.
  // Cloud Run was using a different (smaller) formula — Verde live RPC = 13609,
  // Cloud Run was 3272. The new value is the doc-defined source of truth.
  eiRpc('fn_get_ei_ecp', { p_client_id: clientId }, 'ECP');

export const getEiAmi = (clientId, startDate, endDate) =>
  // Per doc 2.2: data-driven match-set from client's accommodation site_category.
  // Verde match-set = {rv, cabin, glamping}; AMI = 100. RPC adds matched_events,
  // total_high_score_events, property_accommodations alongside the bare ami value
  // (additive — page can show context, not just a number).
  eiRpc('fn_get_ei_ami', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'AMI');

export const getEiMarketingVelocity = (clientId, startDate, endDate) =>
  // ⚠️ Per doc 4.1: avg_lead_days SIGN FLIPS — Cloud Run showed negative
  // (backfill artifact, e.g. -21); RPC now returns positive forward-looking
  // (live ~22). Label the dashboard "avg days before event" not "lead days"
  // to read positive correctly.
  eiRpc('fn_get_ei_marketing_velocity', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'marketing velocity');

export const getEiPlaybookCoverage = (clientId, startDate, endDate) =>
  // Coverage drops from Cloud Run's 100% (denominator=numerator bug) to the
  // honest 83% (238 with playbook / 286 surfaced opportunities). Per doc.
  eiRpc('fn_get_ei_playbook_coverage', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'playbook coverage');

export const getEiSourceDiversity = (clientId, startDate, endDate) =>
  eiRpc('fn_get_ei_source_diversity', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'event source diversity');

export const getEiTimeline = (clientId, startDate, endDate) =>
  // ⚠️ Row order differs from Cloud Run. Express showed serpapi events first;
  // Supabase shows ticketmaster events first. The "top of the list" UI bias
  // shifts unless the page sorts client-side. Set count/total unchanged.
  eiRpc('fn_get_ei_timeline', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'event timeline');

export const getEiWeeklyDensity = (clientId, startDate, endDate) =>
  // Per doc 4.2: tier bands derived from opportunity_score (campaign ≥70,
  // quickwin 50-69, monitor 30-49, low <30). Date range now actually filters
  // (Cloud Run showed weeks ending 2026-06+ regardless of range).
  eiRpc('fn_get_ei_weekly_density', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'weekly density');

export const getEiOpportunities = (clientId, startDate, endDate) =>
  // Surfaced set = status <> 'clustered'. lead_time_days is now-relative (doc 4.5)
  // so values shift as events approach.
  eiRpc('fn_get_ei_opportunities', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'opportunities');

export const getEiPlaybooks = (clientId, startDate, endDate) =>
  // ⚠️ First-row order differs from Cloud Run. Page may show a different
  // "top" playbook recommendation after migration — sort client-side by
  // impact/effort if the order matters for UX.
  eiRpc('fn_get_ei_playbooks', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'playbooks');

export const getEiPlaybookDetail = (playbookId, clientId) =>
  // Migrated 2026-07-01. Per EI Part 2 backend doc (2026-06-23), the RPC was
  // deployed all along — audit had probed only 1-arg signatures. Deployed sig
  // is (p_playbook_id bigint, p_client_id uuid); the client_id is intentional
  // for tenant isolation (WHERE o.client_id = p_client_id — a wrong client_id
  // returns null, not the other client's playbook). Returns 25 fields including
  // `full_brief` and `model_used`.
  eiRpc('fn_get_ei_playbook_detail', { p_playbook_id: playbookId, p_client_id: clientId }, 'playbook detail');

export const getEiCategoryAffinity = async () => {
  // Per doc 5.1: drop-in. ei_category_affinity is a global reference table
  // (no client_id column). The 8 dashboard columns:
  const { data, error } = await supabase.from('ei_category_affinity')
    .select('category, subcategory, tier, rv_affinity, cabin_affinity, glamping_affinity, tent_affinity, notes');
  if (error) wrapSupabaseError(error, 'category affinity');
  return wrapSupabase(data);
};

export const getEiPropertyEventMatch = (clientId, startDate, endDate) =>
  // Per doc 4.4: unscored categories (real events with no opportunity yet) kept
  // visible with null avg_score so pipeline coverage gaps show honestly.
  eiRpc('fn_get_ei_property_event_match', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'property event match');

export const getEiSourceComparison = (clientId, startDate, endDate) =>
  eiRpc('fn_get_ei_source_comparison', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'source comparison');

export const getEiCategoryDistribution = (clientId, startDate, endDate) =>
  // Per doc 4.3: 'Undefined' (Ticketmaster literal) and 'Unclassified' (null)
  // kept as separate buckets. Merge frontend-side if a single bucket is wanted.
  eiRpc('fn_get_ei_category_distribution', { p_client_id: clientId, ...eiDateParams(startDate, endDate) }, 'category distribution');

export const getEiPipelineHealth = (clientId) =>
  eiRpc('fn_get_ei_pipeline_health', { p_client_id: clientId }, 'pipeline health');

// ─── AIM (CampVantage AI Monitor) ────────────────────────────────────────
// Migrated 2026-07-02 per AIM Part 2 Re-Audit backend doc (2026-06-30).
// 34 of 35 helpers route through Supabase RPCs via `aimRpc`. The 35th
// (`getAimRpc`) is a client-side dispatcher per backend doc §5 — it now
// invokes supabase.rpc() by name directly instead of a Cloud Run passthrough.

async function aimRpc(name, params, label) {
  const { data, error } = await rpcReadWithRetry(name, params);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

export const getAimRuns = (clientId, limit) => {
  // RPC accepts (p_client_id) only. `limit` arg preserved on the function
  // signature for backward compat but no longer forwarded — consumers that
  // need a smaller list should slice on the client. TODO(backend): add p_limit.
  void limit;
  return aimRpc('fn_get_aim_runs', { p_client_id: clientId }, 'AIM runs');
};

export const getAimRun = (clientId, runId) =>
  // Wave 3 build (2026-06-30): (client_id, run_id) tenant-isolated.
  aimRpc('fn_get_aim_run', { p_client_id: clientId, p_run_id: runId }, 'AIM run detail');

/**
 * Normalizes an absent dimension argument to SQL NULL.
 *
 * 2026-07-29: the Supabase RPC started treating `p_dimension_key = ''` as a dimension in
 * its own right instead of coalescing it to NULL. Every call site in the app
 * passes `''` to mean "no dimension", so that change silently broke four
 * metrics — P1 and vfi lost their `value_breakdown`, and P11 and P14 returned no
 * snapshot row at all. Normalizing here fixes all of them at once; doing it at
 * the eight call sites would leave the next one to rediscover the trap.
 *
 * See .local/docs/RI_AIM_Develop_vs_Staging_Parity_2026-07-29.md §3.
 *
 * @param {string|null|undefined} dimensionKey Caller's dimension argument.
 * @returns {string|null} The dimension, or null when absent or empty.
 */
const dimensionOrNull = (dimensionKey) => (dimensionKey === '' || dimensionKey == null
  ? null
  : dimensionKey);

export const getAimSnapshot = (clientId, metricCode, dimensionKey, runId) =>
  // Wave 3 build. aim_metric_snapshots is date-keyed; RPC resolves the run's
  // date and returns the latest snapshot on/before it. NULL run → newest.
  aimRpc('fn_get_aim_snapshot', {
    p_client_id: clientId,
    p_metric_code: metricCode,
    p_dimension_key: dimensionOrNull(dimensionKey),
    p_run_id: runId ?? null,
  }, `AIM snapshot ${metricCode}`);

export const getAimSnapshotTrend = (clientId, metricCode, startDate, endDate, dimensionKey) =>
  // Wave 3 build. Date range keyed on snapshot_date.
  aimRpc('fn_get_aim_snapshot_trend', {
    p_client_id: clientId,
    p_metric_code: metricCode,
    p_start_date: startDate ?? null,
    p_end_date: endDate ?? null,
    p_dimension_key: dimensionOrNull(dimensionKey),
  }, `AIM trend ${metricCode}`);

// overall_mention_rate is not in aim_metric_snapshots; backend computes it
// on-demand. Same {snapshot_date, value_numeric, value_breakdown} shape as
// the snapshot-trend RPC, so consumers can render it identically.
export const getAimMentionRateTrend = (clientId, limitRuns) =>
  aimRpc('fn_get_aim_mention_rate_trend_breakdown', {
    p_client_id: clientId,
    p_limit_runs: limitRuns ?? null,
  }, 'AIM mention-rate trend');

// Express URL slug → Postgres function name, ported from the API's RPC_MAP
// (controllers/aim.controller.js). Callers pass the slug — it is what the
// Express routes were keyed on — and Express translated it server-side. That
// translation was never ported during the Supabase migration, so every slug went
// to supabase.rpc() verbatim and came back PGRST202 "function not found",
// silently emptying four cards.
//
// `params: 'date_range'` functions take (p_client_id, p_start_date, p_end_date)
// and no run id. Express supplied a rolling 30-day window when the caller gave
// no dates (its defaultRange helper); with NULL dates these functions return no
// rows, so that default is part of the contract rather than a nicety.
// The date-range functions the dispatcher still maps. Window panels (P5, P11, P14, P16) read their
// run's snapshot instead, so their rolling-window slugs are gone; nothing in the app calls any slug
// below today, and the dispatcher stays only until it is removed as dead code.
const AIM_RPC_SLUGS = {
  'ai-source-conversion': { name: 'aim_ai_source_conversion', params: 'date_range' },
  'leakage-index': { name: 'aim_leakage_index', params: 'date_range' },
  'provider-consensus': { name: 'aim_provider_consensus', params: 'date_range' },
};

const DEFAULT_RANGE_DAYS = 30;

/** `YYYY-MM-DD` for a date `daysAgo` before now, matching Express's defaultRange. */
const isoDaysAgo = (daysAgo) =>
  new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

export const getAimRpc = async (rpcName, { clientId, startDate, endDate, ...extra } = {}) => {
  // Client-side dispatcher (per backend doc §5 — "not a backend function").
  // Invokes any AIM RPC by name with the standard param mapping so old
  // Express-style callers still work.
  const mapped = AIM_RPC_SLUGS[rpcName];
  const resolvedName = mapped ? mapped.name : rpcName;

  let params;
  if (mapped?.params === 'date_range') {
    // Exactly the three parameters the function declares. A caller's run id is
    // deliberately not forwarded — these functions have no p_run_id, and passing
    // one is itself a PGRST202. Call sites use the run only to gate the fetch.
    params = {
      p_client_id: clientId,
      p_start_date: startDate || isoDaysAgo(DEFAULT_RANGE_DAYS),
      p_end_date: endDate || isoDaysAgo(0),
    };
  } else {
    params = { p_client_id: clientId };
    if (startDate) params.p_start_date = startDate;
    if (endDate) params.p_end_date = endDate;
    for (const [k, v] of Object.entries(extra)) {
      if (v !== undefined && v !== null && v !== '') params[`p_${k}`] = v;
    }
  }

  // Read-only by contract — every caller is a data fetch, so the timeout retry applies.
  const { data, error } = await rpcReadWithRetry(resolvedName, params);
  if (error) wrapSupabaseError(error, `AIM RPC ${rpcName}`);
  return wrapSupabase(data);
};

export const getAimGroundTruth = (clientId) =>
  // Wave 3 build. Returns is_current facts only.
  aimRpc('fn_get_aim_ground_truth', { p_client_id: clientId }, 'ground truth');

// 2026-07-08: Deliverable #2 shipped. Upserts a ground-truth fact; if a
// current fact with the same key exists it's retired (is_current=false) and
// the new value inserted. Pass isCurrent=false with empty value to retire
// without replacing.
export const updateAimGroundTruth = async (
  clientId,
  { factKey, factValue, factCategory = null, source = 'manual', isCurrent = true } = {}
) => {
  const { data, error } = await supabase.rpc('fn_update_aim_ground_truth', {
    p_client_id: clientId,
    p_fact_key: factKey,
    p_fact_value: factValue,
    p_fact_category: factCategory,
    p_source: source,
    p_is_current: isCurrent,
  });
  if (error) throw { message: error.message };
  if (data?.error) throw { message: data.message };
  return { error: false, data, message: isCurrent ? 'Ground truth updated' : 'Ground truth retired' };
};

export const getAimGroundTruthChanges = (clientId, limit) => {
  // Wave 4 fix: now includes client_id, campground_name, fact_key_display,
  // fact_category_display (Cloud Run omitted these). RPC signature is
  // (p_client_id) only — no p_limit support today; slice client-side if needed.
  void limit;
  return aimRpc('fn_get_aim_ground_truth_changes', { p_client_id: clientId }, 'ground truth changes');
};

export const getAimHallucinations = (clientId, { runId, providerId, factCategory, limit } = {}) => {
  // Wave 4 fix: redesigned from one-row-per-instance to one row per distinct
  // hallucinated fact with { fact_key, providers[], occurrences, ground_truth }.
  // RPC accepts (p_client_id), (p_client_id, p_run_id), or (p_client_id, p_run_id,
  // p_provider_id). No p_limit / p_fact_category yet — slice/filter client-side.
  void factCategory; void limit;
  const params = { p_client_id: clientId };
  if (runId) params.p_run_id = runId;
  if (providerId) params.p_provider_id = providerId;
  return aimRpc('fn_get_aim_hallucinations', params, 'hallucinations');
};

export const getAimConsensusDetail = (clientId, { runId } = {}) =>
  // Wave 2 rebuild — 2-arg signature (was 1-arg, PGRST202 for frontend calls).
  // Rebuilt from base tables (aim_facts → aim_mentions → aim_responses →
  // aim_providers) since v_aim_fact_accuracy doesn't have run_id.
  aimRpc('fn_get_aim_consensus_detail', { p_client_id: clientId, p_run_id: runId ?? null }, 'consensus detail');

export const getAimKnowledgeRecency = (clientId) =>
  aimRpc('fn_get_aim_knowledge_recency', { p_client_id: clientId }, 'knowledge recency');

export const getAimCategoryConfusion = (clientId, runId) =>
  // Wave 2 fix: added p_run_id param (was 1-arg only).
  aimRpc('fn_get_aim_category_confusion', { p_client_id: clientId, p_run_id: runId ?? null }, 'category confusion');

export const getAimOrganicRankings = (clientId, { runId, limit } = {}) => {
  // Wave 4 fix: deterministic ordering via response_id tiebreak. RPC accepts
  // (p_client_id) or (p_client_id, p_run_id). No p_limit — slice client-side.
  void limit;
  const params = { p_client_id: clientId };
  if (runId) params.p_run_id = runId;
  return aimRpc('fn_get_aim_organic_rankings', params, 'organic rankings');
};

export const getAimOrganicRankingsDetail = (clientId, { responseId } = {}) =>
  // Wave 3 build.
  aimRpc('fn_get_aim_organic_rankings_detail', { p_client_id: clientId, p_response_id: responseId }, 'organic rankings detail');

export const getAimAioPenetration = async (clientId) => {
  // Migrated 2026-06-24. Verified MATCH against Cloud Run (byte-identical).
  const { data, error } = await rpcReadWithRetry('fn_get_aim_aio_penetration', { p_client_id: clientId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getAimPositionVolatility = (clientId) =>
  // Wave 4 fix: kept {client[], competitors[]} split; adds status +
  // runs_available for thin-data clients so the panel can render an empty state.
  aimRpc('fn_get_aim_position_volatility', { p_client_id: clientId }, 'position volatility');

export const getAimSystemRuns = (clientId, limit) =>
  // Wave 3 build (2026-06-30).
  aimRpc('fn_get_aim_system_runs', { p_client_id: clientId, p_limit: limit ?? 20 }, 'system runs');

export const getAimQueueHealth = async (clientId) => {
  // Migrated 2026-06-24. Verified MATCH against Cloud Run (byte-identical).
  const { data, error } = await rpcReadWithRetry('fn_get_aim_queue_health', { p_client_id: clientId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getAimCostSummary = (clientId, runId) =>
  // Wave 5 diagnosed correct — RPC is per-run scoped (Cloud Run blended runs).
  // Verified: sum(cost_summary.cost_usd) reconciles exactly to run.total_cost_usd.
  aimRpc('fn_get_aim_cost_summary', { p_client_id: clientId, p_run_id: runId ?? null }, 'cost summary');

export const getAimErrors = async (clientId, runId) => {
  // Migrated 2026-06-24. Verified MATCH against Cloud Run (byte-identical).
  const { data, error } = await rpcReadWithRetry('fn_get_aim_errors', { p_client_id: clientId, p_run_id: runId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getAimDataCounts = (clientId) =>
  // Wave 4 fix: multi_turn now counts raw aim_multi_turn chains (480 for Verde),
  // was counting a provider×turn aggregation view (33 — wrong denominator).
  aimRpc('fn_get_aim_data_counts', { p_client_id: clientId }, 'data counts');

export const getAimScorecard = (clientId, runId, compareRunId) =>
  // Wave 5 diagnosed correct — per-run provider scorecard (Cloud Run blended).
  aimRpc('fn_get_aim_scorecard', { p_client_id: clientId, p_run_id: runId ?? null, p_compare_run_id: compareRunId ?? null }, 'scorecard');

export const getAimHeatmap = (clientId, runId) =>
  // Wave 5 diagnosed correct — per-run category×provider cells.
  aimRpc('fn_get_aim_heatmap', { p_client_id: clientId, p_run_id: runId ?? null }, 'heatmap');

export const getAimKpis = (clientId, runId, compareRunId) => {
  const params = { p_client_id: clientId };
  if (runId) params.p_run_id = runId;
  if (compareRunId) params.p_compare_run_id = compareRunId;
  return aimRpc('fn_get_aim_kpis', params, 'KPIs');
};

export const getAimSearchLift = async (clientId, runId) => {
  // Migrated 2026-06-24. Per-provider values identical to Cloud Run; only the
  // provider ordering swaps (openai ↔ google_ai positions differ). Frontend
  // keys by provider name so the order doesn't affect the rendered table.
  const { data, error } = await rpcReadWithRetry('fn_get_aim_search_lift', { p_client_id: clientId, p_run_id: runId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getAimSentiment = (clientId, runId) =>
  // Wave 5 diagnosed correct — this run's rates, not blended across all runs.
  aimRpc('fn_get_aim_sentiment', { p_client_id: clientId, p_run_id: runId ?? null }, 'sentiment');

export const getAimProviderCategories = (clientId, providerId, runId) =>
  // Wave 2 fix: added p_provider_id + p_run_id (was 1-arg).
  aimRpc('fn_get_aim_provider_categories', { p_client_id: clientId, p_provider_id: providerId, p_run_id: runId ?? null }, 'provider categories');

export const getAimProviderFacts = (clientId, providerId, runId) =>
  // Wave 2b rebuild — now accepts p_run_id, run-scoped from base tables.
  // Preserves exact accuracy math (inaccurate_facts = is_accurate = false
  // with NULLs excluded; accuracy_rate_pct denominator = is_accurate IS NOT NULL).
  aimRpc('fn_get_aim_provider_facts', { p_client_id: clientId, p_provider_id: providerId, p_run_id: runId ?? null }, 'provider facts');

export const getAimProviderCitations = (clientId, providerId, runId) =>
  // Wave 2 fix: added p_provider_id + p_run_id (was 1-arg).
  aimRpc('fn_get_aim_provider_citations', { p_client_id: clientId, p_provider_id: providerId, p_run_id: runId ?? null }, 'provider citations');

/**
 * The responses behind one heatmap cell, capped at `limit` rows.
 *
 * `provider` is the cell's display name (the heatmap payload carries no provider id, and the RPC
 * accepts either). Rows are one per answer and `limit` caps them; the payload's `mentioned` and
 * `mention_pct` count the answers that name the client, so they never exceed `responses`.
 *
 * @param {string} clientId
 * @param {{category: string, provider: string, runId?: string, limit?: number}} cell
 */
export const getAimHeatmapCell = (clientId, { category, provider, runId, limit = 50 } = {}) =>
  aimRpc('fn_get_aim_heatmap_cell', {
    p_client_id: clientId,
    p_category: category,
    p_provider: provider,
    p_run_id: runId ?? null,
    p_limit: limit,
  }, 'heatmap cell');

export const getAimProviderCoverage = (clientId, runId) =>
  // One row per provider with {count, status} for categories, facts and citations, so the
  // drawer can say why a section is empty instead of guessing.
  aimRpc('fn_get_aim_provider_coverage', { p_client_id: clientId, p_run_id: runId ?? null }, 'provider coverage');

export const getAimCompetitorThreat = (clientId, runId) =>
  // Wave 1: competitor quality filter (length 5-80, structural-junk guards,
  // provider>=4 requirement). Client's own park always retained. No p_limit.
  // `p_run_id` anchors the shared window (the run and the ones before it) so the table and the
  // drawer count the same runs; omitted, the RPC falls back to its own default window.
  aimRpc('fn_get_aim_competitor_threat', {
    p_client_id: clientId,
    ...(runId ? { p_run_id: runId } : {}),
  }, 'competitor threat');

export const getAimShareOfVoice = (clientId, runId) =>
  // Wave 1: same competitor filter. No p_limit — filter itself controls rows.
  aimRpc('fn_get_aim_share_of_voice', { p_client_id: clientId, p_run_id: runId ?? null }, 'share of voice');

export const getAimCompetitorDetail = (clientId, runId, competitorName) =>
  // Wave 3 build. Reads aim_mentions + providers.
  aimRpc('fn_get_aim_competitor_detail', { p_client_id: clientId, p_run_id: runId ?? null, p_competitor_name: competitorName }, 'competitor detail');

export const getAimCitationSources = (clientId, runId, { limit } = {}) =>
  // Wave 1: top-N cap (default 25); client site always pinned.
  aimRpc('fn_get_aim_citation_sources', { p_client_id: clientId, p_run_id: runId ?? null, p_limit: limit ?? 25 }, 'citation sources');

export const getAimAmenityGap = (clientId, runId, { limit } = {}) =>
  // Wave 1: top-N cap (default 25); client site always pinned.
  aimRpc('fn_get_aim_amenity_gap', { p_client_id: clientId, p_run_id: runId ?? null, p_limit: limit ?? 25 }, 'amenity gap');

export const getAimKnowledgeRecencyTimeline = (clientId, { days } = {}) =>
  // Wave 4 fix: was calling snapshot_trend with wrong signature; now converts
  // p_days → start_date server-side and returns a real timeline array (was
  // returning a single object).
  aimRpc('fn_get_aim_knowledge_recency_timeline', { p_client_id: clientId, p_days: days ?? null }, 'knowledge recency timeline');

export const getAimAioFunnel = async (clientId, runId) => {
  // Migrated 2026-06-24. Counts within ~5% of Cloud Run (aio_triggered 17 vs 18,
  // trigger_pct 60.7 vs 64.3, capture_pct 100 vs 94.4) — likely a different
  // de-dup threshold. Same shape, same semantics; acceptable for the funnel panel.
  const { data, error } = await rpcReadWithRetry('fn_get_aim_aio_funnel', { p_client_id: clientId, p_run_id: runId });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// ─── Route Intelligence ──────────────────────────────────────────────────
// 2026-07-08: all 20 RI helpers now hit Supabase RPCs; the URL-keyed axios
// cache (riFetch/riCache/riInFlight/buildRiQs) was removed as dead code.
// See docs/ri-module-supabase-migration-issues-part-2.md for the migration
// history — every helper below is verified value-clean against Cloud Run.

async function riRpc(name, params, label) {
  const { data, error } = await rpcReadWithRetry(name, params);
  if (error) wrapSupabaseError(error, label);
  return wrapSupabase(data);
}

/**
 * The `p_from` / `p_to` pair the range-aware RI functions take (inclusive `YYYY-MM-DD`).
 *
 * A bound that is null/undefined is OMITTED rather than sent as `null`: the backend
 * treats an absent bound as "no limit on that side", and an unranged call then keeps the
 * exact parameter shape it had before the range existed — which is what an environment
 * that has not taken the range-aware function signatures yet still accepts.
 *
 * @param {{ from?: string|null, to?: string|null }} [range]
 * @returns {{ p_from?: string, p_to?: string }}
 */
const riRange = ({ from, to } = {}) => ({
  ...(from ? { p_from: from } : {}),
  ...(to ? { p_to: to } : {}),
});

export const getRiTotalRoutes = (clientId, range) =>
  riRpc('fn_get_ri_total_routes', { p_client_id: clientId, ...riRange(range) }, 'total routes');
export const getRiOriginMarkets = (clientId, range) =>
  // Per backend doc 3.1: unique_states is now 51 (incl DC) via the US-state whitelist,
  // up from Cloud Run's 50. unique_cities/non_us_origins drift slightly due to data growth.
  riRpc('fn_get_ri_origin_markets', { p_client_id: clientId, ...riRange(range) }, 'origin markets');
export const getRiMedianDriveTime = (clientId, range) =>
  riRpc('fn_get_ri_median_drive_time', { p_client_id: clientId, ...riRange(range) }, 'median drive time');
export const getRiAvgGti = (clientId, range) =>
  // Per doc 3.5: booked_guests = 23 (routable bookings only). Cloud Run was 14.
  // Per doc 2.1: avg_fuel_cost + avg_gti shift because gas price moved 4.81 → 4.29.
  riRpc('fn_get_ri_avg_gti', { p_client_id: clientId, ...riRange(range) }, 'average GTI');

export const getRiMapOrigins = (clientId, { includePolylines, limit, from, to } = {}) =>
  // Migrated 2026-07-01 per RI Part 2 Re-Audit doc (2026-06-13). Server-side
  // sort now deterministic: has_booking DESC, origin_lat ASC, origin_lon ASC.
  // Booked origins first so their markers aren't hidden by same-lat pagination
  // races; stable tiebreak on lat/lon.
  riRpc('fn_get_ri_map_origins', {
    p_client_id: clientId,
    p_include_polylines: includePolylines ?? false,
    p_limit: limit ?? 5000,
    ...riRange({ from, to }),
  }, 'map origins');
export const getRiDriveDistribution = (clientId, range) =>
  // Per-bucket route counts drift sub-10% from doc snapshot (data growth + doc 3.4 bucketing).
  riRpc('fn_get_ri_drive_distribution', { p_client_id: clientId, ...riRange(range) }, 'drive distribution');
export const getRiStateTable = (clientId, range) =>
  // Per-state totals drift slightly; congestion_min values lower because of newer congestion data.
  riRpc('fn_get_ri_state_table', { p_client_id: clientId, ...riRange(range) }, 'state table');
export const getRiOriginDetail = (clientId, { originLat, originLon, from, to }) =>
  // Per doc 5.3 and 2.1: takes coordinates; fuel_cost_roundtrip shifts with gas price.
  // The route is always returned; the range only filters its booked-guest list.
  riRpc('fn_get_ri_origin_detail', {
    p_client_id: clientId,
    p_origin_lat: originLat,
    p_origin_lon: originLon,
    ...riRange({ from, to }),
  }, 'origin detail');
export const getRiParkMarker = (clientId) =>
  // Per doc § "All 20 Endpoints": clients-table drop-in. RPC mode here just for shape parity.
  // Returns id/name/campground_name/lat/lon/city/state/country/property_type. Cloud Run
  // returned a 5-field subset; the extras are additive and consumers ignore them.
  (async () => {
    const { data, error } = await supabase.from('clients')
      .select('id, name, campground_name, latitude, longitude, city, state, country, property_type')
      .eq('id', clientId).limit(1).single();
    if (error) wrapSupabaseError(error, 'park marker');
    return wrapSupabase(data);
  })();

export const getRiCorridors = (clientId, { sort, from, to } = {}) =>
  // Per doc 3.3: CCE = revenue / booking_sessions × 100 (defined assumption, may not exactly
  // match Cloud Run's composite if it was different). Length 71 → 87 reflects data growth.
  riRpc('fn_get_ri_corridors', { p_client_id: clientId, p_sort: sort ?? null, ...riRange({ from, to }) }, 'corridors');

export const getRiTripEconomics = (clientId, range) =>
  // Per doc 2.1: fuel_cost_roundtrip / gti / trip_cost_ratio / rate_headroom_pct shift
  // because gas price moved $4.81 → $4.29. Guest names come UPPERCASE from Supabase
  // (booking_sessions stores them that way); Cloud Run had a Title Case adapter.
  riRpc('fn_get_ri_trip_economics', { p_client_id: clientId, ...riRange(range) }, 'trip economics');

export const getRiCongestionLeaderboard = (clientId, range) =>
  // Migrated 2026-07-01. congestion_tax_pct definition now delay/duration_static
  // (the correct "% over free-flow baseline") = 30.2%. Cloud Run used
  // delay/total_traffic_time = 23.2% which understated it (they reconcile:
  // 0.302 / 1.302 = 0.232). slow_jam_segments is now a real count from
  // travelAdvisory.speedReadingIntervals (37 of 55 for Verde top row);
  // slow_jam_is_proxy=false marks it as counted, not proxied.
  riRpc('fn_get_ri_congestion_leaderboard', { p_client_id: clientId, ...riRange(range) }, 'congestion leaderboard');

export const getRiCongestionByCorridor = (clientId, range) =>
  // Migrated 2026-07-01. Ordered volume-first (most-traveled corridor first):
  // Verde top = I-17 N with ~1,244 routes, replacing the arbitrary low-sample
  // corridors that had floated up on avg congestion. Same 36 corridors (≥3
  // routes filter); documented as show-all.
  riRpc('fn_get_ri_congestion_by_corridor', { p_client_id: clientId, ...riRange(range) }, 'congestion by corridor');

export const getRiConversionByDistance = (clientId, range) =>
  // Sub-1% drift on per-bucket session counts vs Cloud Run (data growth, within tolerance).
  riRpc('fn_get_ri_conversion_by_distance', { p_client_id: clientId, ...riRange(range) }, 'conversion by distance');
export const getRiBookedGuests = (clientId, { page, pageSize, from, to } = {}) =>
  // Migrated 2026-07-01. Deterministic sort: reservation_value DESC NULLS LAST,
  // check_in_date DESC, booking_session_id. Top-guest-first preserved with
  // stable tiebreaks so pagination doesn't repeat/skip rows.
  riRpc('fn_get_ri_booked_guests', {
    p_client_id: clientId,
    p_page: page ?? 1,
    p_page_size: pageSize ?? 50,
    ...riRange({ from, to }),
  }, 'booked guests');

// ─── Fuel wave (migrated 2026-07-01 per RI Part 2 Re-Audit) ─────────────────
// Backend repointed all 4 fuel helpers from ri_route_places (stations along
// the whole drive route — mislabeled distances 806-2,266 mi) to
// ri_property_places WHERE waypoint_type='property' (stations around the park,
// 0.1-30.5 mi). This fixes the FuelCheapest "Amarillo 1,510mi" bug in Cloud Run.

export const getRiFuelSummary = (clientId) =>
  // Shape change: now returns { sampled_week, is_stale, park, feeder_corridors }
  // instead of the old bare waypoint array. Verde: 11 stations park-area,
  // cheapest $4.34, 7 feeder corridors (was 85 when reading the wrong table).
  riRpc('fn_get_ri_fuel_summary', { p_client_id: clientId }, 'fuel summary');

export const getRiFuelCheapest = (clientId) =>
  // Deduped to each station's most-recent sampled_week; miles_from_park is now
  // real (0-10 mi range) instead of the stale route-distance mislabel.
  riRpc('fn_get_ri_fuel_cheapest', { p_client_id: clientId }, 'cheapest stations');

export const getRiFuelTrend = (clientId, { from, to } = {}) =>
  // 12 weekly points bucketed by sampled_week; min_regular is now the
  // park-area cheapest, not a route-anywhere outlier.
  riRpc('fn_get_ri_fuel_trend', { p_client_id: clientId, p_from: from ?? null, p_to: to ?? null }, 'fuel trend');

export const getRiFuelByMarket = (clientId, range) =>
  // Follows the picker range (unlike Summary/Cheapest, which are current-price reads).
  // Confidence tiers: all origin states are shown with their tier —
  // ≥15 established / ≥5 emerging / else sparse.
  riRpc('fn_get_ri_fuel_by_market', { p_client_id: clientId, ...riRange(range) }, 'fuel by market');
export const getRiHealth = (clientId) =>
  riRpc('fn_get_ri_health', { p_client_id: clientId }, 'ri health');

// =====================================================
// WEATHER INTELLIGENCE APIs
// =====================================================

export const getWeatherIntelligence = async (clientId, asOfDate = null) => {
  // Single call returns all 26 panels, verdict, park info. Pre-computed nightly.
  // Pass p_as_of to view a historical snapshot; omit for today.
  const params = { p_client_id: clientId };
  if (asOfDate) params.p_as_of = asOfDate;
  const { data, error } = await supabase.rpc('fn_get_weather_intelligence', params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWeatherDates = async (clientId) => {
  // Returns snapshot_dates (for the as-of picker) and detail_from/detail_to
  // (for the Detail tab range control). No date params — just the client.
  const { data, error } = await supabase.rpc('fn_get_weather_dates', {
    p_client_id: clientId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWeatherDetail = async (clientId, fromDate, toDate) => {
  // Detail tab's own range control. Capped at 400 rows per call;
  // response returns capped: true when the range was trimmed.
  const { data, error } = await supabase.rpc('fn_get_weather_detail', {
    p_client_id: clientId,
    p_from: fromDate,
    p_to: toDate,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

